// ============================================================
// motor.js
// EV Powertrain Simulator — core 모듈
// 모터 물리 — FOC, 토크/속도, 손실 모델
// Extracted from ev-sim_v1_0_Verification_46.jsx (Phase 2)
// ============================================================

// [Fix-Issue1] math_utils.js 의존 함수 명시적 import — 공유 스코프 암묵 의존 제거
import { derateScale } from './math_utils.js';

// [Fix-Issue1] battery.js 의존 함수 명시적 import
// 순환 의존 없음: battery.js → (battery_lut, physics_consts) 이며 motor.js를 참조하지 않음
import { P_dis_max_T } from './battery.js';

// ── Rs_T ──
/**
 * 권선 저항 — 온도 보정 + AC 표피효과/근접효과 보정
 *
 * 기존: R_dc(T) = Rs × (1 + α_Cu × ΔT)   — DC 저항 온도 보정만
 * 추가: R_ac(T,f) = R_dc(T) × k_ac(f_e)
 *   k_ac = 1 + k_skin × (f_e / f_skin_ref)^n_skin
 *   f_e    : 전기 주파수 [Hz]
 *   f_skin_ref : 표피효과 기준 주파수 [Hz] (기본 200 Hz — 8극 3000 rpm 기준)
 *   k_skin : 표피효과 계수 (기본 0.10 — 구리 헤어핀 권선 대표값)
 *   n_skin : 멱지수 (기본 2.0 — 원형 도체 이론값)
 *
 * 파라미터:
 *   params.skin_effect_enable : true 시 AC 보정 적용 (기본 false → 기존 동작 유지)
 *   params.f_skin_ref         : 기준 주파수 [Hz]
 *   params.k_skin             : 표피효과 계수
 *   params.n_skin             : 멱지수
 *   params.omega_e_for_Rs     : 현재 전기각속도 [rad/s] — computeMotorLosses에서 주입
 *
 * 참고: Mellor et al. (2011), "Influence of …eddy current losses in …hairpin windings"
 */
function Rs_T(T_motor, params) {
  const R_dc = params.Rs * (1 + params.alpha_Cu * (T_motor - params.T_ref));
  if (!params.skin_effect_enable) return R_dc;
  const omega_e    = params.omega_e_for_Rs || 0;
  const f_e        = Math.abs(omega_e) / (2 * Math.PI);
  const f_ref      = params.f_skin_ref  || 200;   // Hz
  const k_skin     = params.k_skin      || 0.10;
  const n_skin     = params.n_skin      || 2.0;
  const k_ac       = 1 + k_skin * Math.pow(Math.max(0, f_e / f_ref), n_skin);
  return R_dc * k_ac;
}
/**
 * 영구자석 쇄교 자속 — 온도 보정 (자석 감자 모델)
 *
 * NdFeB 자석: 온도 상승 시 잔류 자속(Br)이 선형 감소한다.
 *   λ_pm(T) = λ_pm_ref × (1 − k_lambda × (T − T_ref))
 *   k_lambda ≈ 0.0012 /°C (NdFeB 대표값)
 * T > T_ref에서 감소, T < T_ref에서 증가 (정격 대비).
 *
 * @param {number} T_motor - 모터 온도 [°C]
 * @param {object} params
 *   params.lambda_pm  - 기준 쇄교 자속 [Wb]
 *   params.k_lambda   - 감자 계수 [/°C]
 *   params.T_ref      - 기준 온도 [°C]
 * @returns {number} - 보정된 쇄교 자속 [Wb]
 */
function lambda_pm_T(T_motor, params) { return params.lambda_pm * (1 - params.k_lambda * (T_motor - params.T_ref)); }
// [Fix-Motor-R] R_int_T 제거 — battery.js 단일 구현 사용 (motor.js의 동일 로직은 dead code였음)

// ── I_max_T ──
/**
 * 인버터 온도 의존 최대 전류 산출
 *
 * 인버터 온도가 T_inv_derate_start를 초과하면 선형 디레이팅:
 *   I_max(T) = I_max_rms × derateScale(T, T_start, T_end)
 * IGBT 접합 온도 제한으로 인한 전류 감소를 모사한다.
 *
 * @param {number} T_inv  - 인버터 온도 [°C]
 * @param {object} params
 *   params.I_max_rms          - 정격 최대 전류 [A]
 *   params.T_inv_derate_start - 디레이팅 시작 온도 [°C]
 *   params.T_inv_derate_end   - 완전 차단 온도 [°C]
 * @returns {number}           - 디레이팅 후 최대 전류 [A]
 */
function I_max_T(T_inv, params) {
  return params.I_max_rms * derateScale(T_inv, params.T_inv_derate_start, params.T_inv_derate_end);
}

// ── T_max_motor_T ──
/**
 * 모터 온도 의존 최대 출력 토크 산출
 *
 * 모터 권선 온도가 T_motor_derate_start를 초과하면 선형 디레이팅:
 *   T_max(T) = T_max_motor × derateScale(T, T_start, T_end)
 *
 * @param {number} T_motor - 모터 권선 온도 [°C]
 * @param {object} params
 *   params.T_max_motor          - 정격 최대 토크 [Nm]
 *   params.T_motor_derate_start - 디레이팅 시작 온도 [°C]
 *   params.T_motor_derate_end   - 완전 차단 온도 [°C]
 * @returns {number}             - 디레이팅 후 최대 토크 [Nm]
 */
function T_max_motor_T(T_motor, params) {
  return params.T_max_motor * derateScale(T_motor, params.T_motor_derate_start, params.T_motor_derate_end);
}

// ── motorTorqueSpeed ──
/**
 * 모터 토크-속도 제한 곡선 연산 (4중 토크 한계 중 최솟값)
 *
 * 다음 네 가지 토크 한계 중 가장 낮은 값에 throttle을 곱해 요구 토크를 결정한다:
 *   1. T_max_motor(T): 모터 권선 온도 기반 열적 한계
 *   2. T_power_limit = P_max_wheel / (η_dl × ω): 구동계 최대 전력 한계
 *   3. T_batt_lim = P_dis_max_T / ω: 배터리 방전 전력 한계
 *   4. f_inv: 인버터 온도 디레이팅 계수
 * BMS 방전 전류 한계 및 극저온 배터리 보호도 적용한다.
 *
 * @param {number} omega_m  - 모터 각속도 [rad/s]
 * @param {number} throttle - 가속 페달 개도 [0, ~1.15] (드라이브 모드 최대 비율까지)
 * @param {object} params   - 차량 파라미터
 * @param {number} [T_motor=25] - 모터 온도 [°C]
 * @param {number} [T_inv=25]   - 인버터 온도 [°C]
 * @param {number} [T_batt=25]  - 배터리 온도 [°C]
 * @returns {number}            - 요구 토크 [Nm]
 */
function motorTorqueSpeed(omega_m, throttle, params, T_motor = 25, T_inv = 25, T_batt = 25) {
  const omega   = Math.max(0.1, omega_m);
  const eta_dl  = params.eta_driveline || 0.95;
  // [A21-15-01] T_power_limit: 모터 샤프트 기준 토크 상한
  const T_power_limit = (params.P_max_wheel || 220000) / eta_dl / omega;
  const T_max_m       = T_max_motor_T(T_motor, params);
  const f_inv         = derateScale(T_inv,
    params.T_inv_derate_start || 80,
    params.T_inv_cutoff       || 100);
  const P_dis      = P_dis_max_T(T_batt, params);
  // [A21-15-02] T_batt_lim: 단위 수정
  let T_batt_lim = P_dis / omega;
  // [A10] bms_i_discharge_max → 토크 상한 (I_max × V_dc / ω)
  if (params.bms_i_discharge_max) {
    const P_dis_I = params.bms_i_discharge_max * (params.V_dc || 400);
    T_batt_lim = Math.min(T_batt_lim, P_dis_I / omega);
  }
  // [A10] bms_v_cell_min → SOC 임계 도달 시 완전 차단 (방전 보호)
  const soc_cutoff_v = params.bms_soc_discharge_cutoff || 0.05;
  // bms_v_cell_min으로부터 SOC 근사: 2.8V=0%, 4.2V=100% 선형 근사
  const soc_from_vmin = Math.max(0, Math.min(0.15,
    ((params.bms_v_cell_min || 2.8) - 2.5) / (4.2 - 2.5)));
  const eff_soc_cutoff = Math.max(soc_cutoff_v, soc_from_vmin);
  // [Fix-M1] 극저온 배터리 데레이팅 임계값 수정
  // 기존: (T_batt_derate_start - 40) = 5°C → 정상 운행 온도에서도 50% 컷 발생
  // 수정: 전용 파라미터 T_batt_cold_limit (기본 -10°C) — 실측 LiNMC 극저온 한계 대응
  if (T_batt <= (params.T_batt_cold_limit ?? -10)) T_batt_lim *= 0.5;
  // [BugFix-A02-01] throttle 클램프: 기존 Math.min(1, throttle)은 SPORT(1.15)/OFF_ROAD(1.0) 고배율을 잘라냄
  //   수정: 모드별 maxRatio 상한까지 허용 → T_max_eff 자체가 물리 한계이므로 안전
  const mode = (params && params.drive_mode) || 'NORMAL';
  const modeMaxRatio = { ECO: params.eco_max_torque_ratio||0.7,
                         NORMAL: params.normal_max_torque_ratio||1.0,
                         SPORT: params.sport_max_torque_ratio||1.15,
                         SNOW: params.snow_max_torque_ratio||0.55,
                         OFF_ROAD: params.offroad_max_torque_ratio||1.0 }[mode] || 1.0;
  const T_max_eff  = Math.min(T_max_m, T_power_limit, T_batt_lim) * f_inv;
  const thr_clamped = Math.max(0, Math.min(modeMaxRatio + 0.05, throttle));
  return Math.max(0, T_max_eff * thr_clamped);
}

// ── saturatedInductance ──
/**
 * 자기 포화 인덕턴스 모델 — Ld(Id), Lq(Iq)
 *
 * 고전류 영역에서 철심 포화로 인덕턴스 감소:
 *   Ld(Id) = Ld0 / (1 + ks_d · |Id| / I_rated)
 *   Lq(Iq) = Lq0 / (1 + ks_q · |Iq| / I_rated)
 *
 * 기본값(ks=0) 시 고정 Ld/Lq 반환 → 기존 동작 유지
 *
 * @param {number} Id              - d축 전류 [A]
 * @param {number} Iq              - q축 전류 [A]
 * @param {object} params          - 차량 파라미터
 * @param {number} [T_motor=25]    - 모터 온도 [°C] (기본: 25°C, 온도 보정 없음)
 * @returns {{ Ld, Lq, Ld0, Lq0, sat_d, sat_q }}
 */
function saturatedInductance(Id, Iq, params, T_motor = params.T_motor ?? 25) {
  // [Fix-Ldq-Temp] Ld/Lq 온도 의존성 추가
  // NdFeB 영구자석 및 철심: 온도↑ → 비투자율(μr)↓ → 인덕턴스 소폭 감소
  // 경험식: L(T) = L(T_ref) × (1 − k_L_T × (T_motor − T_ref))
  //   k_L_T ≈ 0.0010~0.0015 /°C  (NdFeB 철심, Morimoto 2003 참조)
  //   T_motor가 없으면(기본 25°C) 온도 보정 없음 — 기존 동작 유지
  const T_motor_sat = T_motor;  // 명시적 파라미터 — _T_motor_for_sat 숨겨진 채널 제거
  const k_L_T  = params.k_Ldq_temp || 0.0012;  // 인덕턴스 온도계수 [/°C]
  const T_ref  = params.T_ref || 25;
  const f_L_T  = Math.max(0.7, 1 - k_L_T * Math.max(0, T_motor_sat - T_ref));
  const Ld0 = (params.Ld || 0.0003) * f_L_T;
  const Lq0 = (params.Lq || 0.0006) * f_L_T;

  if (!params.magnetic_saturation_enable) {
    return { Ld: Ld0, Lq: Lq0, Ld0, Lq0, sat_d: 1.0, sat_q: 1.0 };
  }

  const ks_d    = params.ks_d    || 0.25;  // d축 포화 계수 (기본: 25%/I_rated)
  const ks_q    = params.ks_q    || 0.40;  // q축 포화 계수 (기본: 40%/I_rated — Lq가 더 빠르게 포화)
  const I_rated = params.I_rated || params.I_max_rms || 600;  // 기준 전류 [A]

  // 포화 인자 (1차 근사, 연속·미분 가능)
  const sat_d = 1 / (1 + ks_d * Math.abs(Id) / I_rated);
  const sat_q = 1 / (1 + ks_q * Math.abs(Iq) / I_rated);

  const Ld_sat = Ld0 * sat_d;
  const Lq_sat = Lq0 * sat_q;

  // 물리 제약: Ld > 0, Lq > Ld (IPMSM 돌극비 유지)
  const Ld_out = Math.max(Ld0 * 0.3, Ld_sat);
  const Lq_out = Math.max(Lq0 * 0.3, Math.max(Ld_out * 1.1, Lq_sat));

  // [Fix-CrossSat] 교차 포화 (Cross-Saturation) 항 — 기본값 0.08 (활성)
  //   실제 IPMSM: d축 자속이 Iq에도 의존 (∂Ψd/∂Iq ≠ 0)
  //   Ldq = ∂Ψd/∂Iq = ks_dq × Ld0 × Lq0 / sqrt(Id²+Iq²+ε)  (근사)
  //   기존: ks_dq=0.0 → 교차 포화 비활성 (SPMSM처럼 동작)
  //   수정: ks_dq=0.08 — Bianchi & Bolognani (2002) IPMSM 측정값 기준
  //   비활성: params.ks_dq=0 으로 설정
  const ks_dq   = params.ks_dq !== undefined ? params.ks_dq : 0.08; // 교차 포화 계수 (기본 활성)
  const I_total = Math.sqrt(Id * Id + Iq * Iq);
  const Ldq     = I_total > 1
    ? ks_dq * Ld0 * Lq0 / (I_total + 1e-9)
    : 0;

  return { Ld: Ld_out, Lq: Lq_out, Ldq, Ld0, Lq0, sat_d, sat_q };
}

// ── computeMTPV (v2 — 전압 타원 파라미터 탐색, Iq ≠ 0 완전 해) ──
// [Fix-MTPV-Cache] 모듈 레벨 캐시: omega_e 변화 < 1 rad/s 이면 재사용
//   FW 진입 빈도 높은 시나리오(고속 정속 주행)에서 N=120 순회 연산 부담 절감.
//   캐시 키: omega_e(정수 반올림) + V_dc + T_motor(정수). params는 시뮬 중 불변 가정.
const _mtpvCache = { key: null, result: null };
/**
 * 기존 구현의 문제:
 *   Iq=0으로 고정한 채 전압 타원을 풀어 Id_mtpv를 구함.
 *   → Iq ≠ 0인 실제 MTPV 궤적(토크-속도 포물선)에서 최대 5~10% 토크 오차 발생.
 *
 * 수정: 전압 타원을 각도 파라미터 φ로 표현 후 T(φ)를 최대화
 *
 *   전압 타원 파라미터 형식:
 *     Id(φ) = -λpm/Ld  +  (Vmax/ωe)/Ld · cos φ          [d축]
 *     Iq(φ) =             (Vmax/ωe)/Lq · sin φ   ≥ 0    [q축, 전동 모드]
 *   φ ∈ [0, π]  →  Iq ≥ 0 보장
 *
 *   토크:
 *     T(φ) = 1.5p [λpm·Iq(φ) + (Ld−Lq)·Id(φ)·Iq(φ)]
 *
 *   최적화:
 *     φ* = argmax T(φ)  subject to  Id²+Iq² ≤ I_max²
 *
 *   N=120점 균등 탐색 → 각도 분해능 1.5° → 토크 오차 < 0.1%
 *   (참고: Morimoto 1994; Soong & Ertugrul 2002)
 */
function computeMTPV(omega_e, V_dc, params, T_motor) {
  // [Fix-MTPV-Cache] 캐시 조회 (omega_e 1 rad/s, T_motor 1°C 해상도)
  const cacheKey = `${Math.round(omega_e)}|${V_dc}|${Math.round(T_motor ?? 25)}`;
  if (_mtpvCache.key === cacheKey) return _mtpvCache.result;

  const lpm   = lambda_pm_T(T_motor, params);
  const V_max = V_dc * (params.fw_v_margin || 0.95) * (1 / Math.sqrt(3));
  const Ld    = params.Ld;
  const Lq    = params.Lq;
  const p     = params.poles / 2;
  const I_max = params.I_max_rms || 600;

  // 전기 각속도가 너무 낮으면 약자속 불필요 → 조기 반환
  if (omega_e < 1) return { Id: 0, Iq: 0, regime: 'too_slow', valid: false };

  // 전압 타원 반축 [A]
  const r_d  = V_max / (omega_e * Ld);   // d축 반축
  const r_q  = V_max / (omega_e * Lq);   // q축 반축
  const Id_c = -lpm / Ld;                // 전압 타원 중심 d좌표 (= −ψpm/Ld)

  // 타원 중심이 전류 원 밖이면 전압 제약 초과 → 출력 불가
  if (Id_c + r_d < -I_max) {
    return { Id: 0, Iq: 0, regime: 'voltage_limit_exceeded', valid: false };
  }

  // φ ∈ [0, π]: N점 균등 탐색으로 T(φ) 최대점 탐색
  // [Fix-INFO-2] 탐색 해상도 파라미터화 — params.mtpv_search_n (기본값 120, 1.5° 분해능 → 토크 오차 <0.1%)
  //   성능 민감 환경에서 낮추거나, 정밀도 요구 시 높여 사용 가능. 기본값 유지로 하위 호환 보장.
  const N = params.mtpv_search_n || 120;
  let best_T  = -Infinity;
  let best_Id = 0, best_Iq = 0;

  for (let k = 0; k <= N; k++) {
    const phi  = (Math.PI * k) / N;
    const Id_k = Id_c + r_d * Math.cos(phi);
    const Iq_k = r_q  * Math.sin(phi);          // sin φ ≥ 0 → 전동 모드 Iq ≥ 0

    // 전류 원 제약 (0.1% 여유 허용)
    if (Id_k * Id_k + Iq_k * Iq_k > I_max * I_max * 1.001) continue;

    const T_k = 1.5 * p * (lpm * Iq_k + (Ld - Lq) * Id_k * Iq_k);
    if (T_k > best_T) { best_T = T_k; best_Id = Id_k; best_Iq = Iq_k; }
  }

  if (best_T <= 0) {
    const r = { Id: 0, Iq: 0, regime: 'no_positive_torque', valid: false };
    _mtpvCache.key = cacheKey; _mtpvCache.result = r; return r;
  }
  const result = { Id: best_Id, Iq: best_Iq, regime: 'mtpv', T_available: +best_T.toFixed(2), valid: true, V_max };
  _mtpvCache.key = cacheKey; _mtpvCache.result = result;
  return result;
}

// ── getDemagLimit ──
/**
 * 2D 감자 한계 LUT: f(T_motor) → Id_demag_limit
 *
 * NdFeB 보자력(Hcj)은 온도 상승에 따라 급감 → 고온에서 더 작은 |Id|에서도
 * 불가역 감자(demagnetization) 발생. 단일 상수 Id_demag_limit는 저온 과보수 /
 * 고온 과허용 문제를 동시에 가짐.
 *
 * 입력: params.demag_lut = [[T0, Id_lim0], [T1, Id_lim1], ...] (온도 오름차순)
 *   예) [[-30, 600], [25, 510], [80, 400], [120, 310], [150, 220]]
 *   단위: T [°C], Id_lim [A]  (양수값 — 사용 시 부호 반전해서 클램프)
 *
 * fallback: demag_lut 없으면 params.Id_demag_limit || I_max×0.85 (기존 동작 유지)
 *
 * 참고: Arnold et al. (2006), "Temperature-dependent demagnetization …NdFeB"
 */
function getDemagLimit(T_motor, I_max, params) {
  if (!params.demag_lut || !Array.isArray(params.demag_lut) || params.demag_lut.length < 2) {
    return params.Id_demag_limit || (I_max * 0.85);
  }
  const lut = params.demag_lut;
  const T   = T_motor ?? 25;
  if (T <= lut[0][0])                  return lut[0][1];
  if (T >= lut[lut.length - 1][0])     return lut[lut.length - 1][1];
  for (let i = 0; i < lut.length - 1; i++) {
    if (T >= lut[i][0] && T <= lut[i + 1][0]) {
      const frac = (T - lut[i][0]) / (lut[i + 1][0] - lut[i][0]);
      return lut[i][1] + frac * (lut[i + 1][1] - lut[i][1]);
    }
  }
  return params.Id_demag_limit || (I_max * 0.85);
}

// ── computeMTPA ── (독립 함수, Morimoto 1994)
/**
 * MTPA(Maximum Torque Per Ampere) 전류 지령 산출 — 독립 함수
 *
 * 릴럭턴스 토크를 포함한 IPMSM 최적 전류 궤적을 반환한다.
 *   T = 1.5·p·[λpm·Iq + (Ld−Lq)·Id·Iq]
 *   MTPA 조건 (∂T/∂β|I=const = 0):
 *     Id* = (λpm − √(λpm² + 8·ΔL²·Iq²)) / (4·ΔL),  ΔL = Lq−Ld > 0 (IPMSM)
 *     SPMSM(Ld≈Lq): Id* = 0
 *
 * 포화 인덕턴스(Id=0, Iq=Iq_mtpa 근사)와 온도 보정 자속·전류 한계를 반영한다.
 * 탈자 한계(getDemagLimit)로 Id를 클램프하여 자석 불가역 감자를 방지한다.
 *
 * ─ 전압/약계자 판단은 포함하지 않음 — computeCurrentReferenceWithMTPV 에서 수행 ─
 *
 * @param {number} T_req     - 요구 토크 [N·m]
 * @param {object} params    - 차량 파라미터
 * @param {number} T_motor   - 모터 온도 [°C]
 * @returns {{ Id, Iq, Id_raw, Iq_raw, regime: 'mtpa'|'mtpa_spmsm' }}
 */
function computeMTPA(T_req, params, T_motor) {
  const p       = params.poles / 2;
  const lpm     = lambda_pm_T(T_motor, params);
  const I_max   = I_max_T(T_motor, params) || params.I_max_rms;

  // q축 전류 초기 추정 (릴럭턴스 토크 무시 1차 근사)
  const Iq_raw  = T_req / (1.5 * p * lpm);

  // 포화 인덕턴스: d축 전류 = 0, q축 전류 = Iq_raw 로 근사
  const sat0    = saturatedInductance(0, Iq_raw, params, T_motor);
  const Ld      = sat0.Ld;
  const Lq      = sat0.Lq;
  const dL      = Lq - Ld; // > 0 for IPMSM

  let Id_raw;
  let regime;
  if (Math.abs(dL) > 1e-6) {
    // IPMSM: 릴럭턴스 토크 최대화 — Id < 0
    const disc = lpm * lpm + 8 * dL * dL * Iq_raw * Iq_raw;
    Id_raw  = (lpm - Math.sqrt(disc)) / (4 * dL); // 음수 결과 ✓
    Id_raw  = Math.max(-I_max * 0.7, Id_raw);      // 전류 한계 클램프
    regime  = 'mtpa';
  } else {
    // SPMSM (Ld≈Lq): 릴럭턴스 토크 없음 → Id* = 0
    Id_raw  = 0;
    regime  = 'mtpa_spmsm';
  }

  // 탈자 보호
  const Id_demag  = getDemagLimit(T_motor, I_max, params);
  const Id        = Math.max(-Id_demag, Id_raw);
  const Iq        = Math.min(Iq_raw, I_max);

  return { Id, Iq, Id_raw, Iq_raw, regime };
}

// ── computeCurrentReferenceWithMTPV ──
/**
 * MTPA → 약계자 → MTPV 3단계 전류 지령 생성
 *
 * 동작 순서:
 *   1. MTPA: 요구 토크에 대한 최적 (Id, Iq) 계산
 *   2. 전압 검증: V_mag_mtpa ≤ V_max이면 MTPA 출력
 *   3. 약계자(FW): 이분법(12회)으로 전압 타원 + 전류 원의 교점 탐색
 *   4. MTPV: 고속 약계자 영역에서 토크 최대화
 * 각 단계에서 감자 한계(getDemagLimit)를 적용해 자석 보호.
 *
 * @param {number} omega_e  - 전기각속도 [rad/s]
 * @param {number} T_req    - 요구 토크 [Nm]
 * @param {number} V_dc     - DC 링크 전압 [V]
 * @param {object} params   - 차량 파라미터
 * @param {number} T_motor  - 모터 온도 [°C]
 * @returns {{ Id, Iq, regime: 'mtpa'|'mtpv'|'field_weakening_vc'|'current_limited', [T_available, V_mag, V_max] }}
 */
function computeCurrentReferenceWithMTPV(omega_e, T_req, V_dc, params, T_motor) {
  const p   = params.poles / 2;
  const lpm = lambda_pm_T(T_motor, params);
  const I_max = I_max_T(T_motor, params) || params.I_max_rms;

  // ── MTPA 위임: 독립 computeMTPA 호출 ──
  const mtpa = computeMTPA(T_req, params, T_motor);
  const Id_mtpa_safe = mtpa.Id;
  const Iq_mtpa      = mtpa.Iq;

  // MTPA 전압 검증 (포화 인덕턴스 기준)
  const sat0 = saturatedInductance(0, Iq_mtpa, params, T_motor);
  const Ld   = sat0.Ld;
  const Lq   = sat0.Lq;
  const V_d_mtpa   = -omega_e * Lq * Iq_mtpa;
  const V_q_mtpa   = omega_e * (Ld * Id_mtpa_safe + lpm);
  const V_mag_mtpa = Math.sqrt(V_d_mtpa * V_d_mtpa + V_q_mtpa * V_q_mtpa);
  const V_max      = V_dc * (params.fw_v_margin || 0.95) * (1 / Math.sqrt(3));

  if (V_mag_mtpa <= V_max && Iq_mtpa <= I_max) {
    return { Id: Id_mtpa_safe, Iq: Math.min(Iq_mtpa, I_max), regime: mtpa.regime, V_mag: V_mag_mtpa, V_max };
  }
  if ((params.mtpv_enable !== false) && (params.fw_enable !== false)) {
    const mtpv = computeMTPV(omega_e, V_dc, params, T_motor);
    if (mtpv.valid) {
      const T_mtpv = 1.5 * p * (lpm * mtpv.Iq + (params.Ld - params.Lq) * mtpv.Id * mtpv.Iq);
      if (T_mtpv >= T_req * 0.95 || T_mtpv >= T_req - 10) {
        return { Id: mtpv.Id, Iq: mtpv.Iq, regime: 'mtpv', T_available: T_mtpv, V_max: mtpv.V_max };
      }
    }
  }
  if (params.fw_enable !== false) {
    // [Enhancement-FW] 전압원 기반 약계자 전류 계산
    //   전압 타원 제약: (ωe·Ld·Id + ωe·λpm)² + (ωe·Lq·Iq)² = Vmax²
    //   전류원 제약:    Id² + Iq² = I_max²
    //   위 두 제약의 교점에서 Id_fw, Iq_fw 산출 (bisection)
    //   참고: Morimoto (1994), Soong & Ertugrul (2002)
    const V_max_fw = V_max;
    let Id_lo = -I_max, Id_hi = 0;
    let Id_fw_vc = -I_max * 0.3, Iq_fw_vc = 0;
    // 이분법 10회 반복으로 전압 타원 상 Id 수렴
    for (let iter = 0; iter < 12; iter++) {
      const Id_mid = (Id_lo + Id_hi) / 2;
      const Iq_mid_sq = Math.max(0, I_max * I_max - Id_mid * Id_mid);
      const Iq_mid    = Math.sqrt(Iq_mid_sq);
      // [Fix-FW-SatLdq] 이분법 중간점(Id_mid, Iq_mid)에서 포화 인덕턴스 재산출
      //   기존: 외부 sat0(MTPA 전류 기준)의 Lq/Ld를 루프 전체에 고정 사용
      //         → 강한 포화 영역(고전류 d축)에서 전압 제약 과소평가 가능
      //   수정: 루프 내 saturatedInductance(Id_mid, Iq_mid) 호출로 점별 포화 반영
      const sat_mid   = saturatedInductance(Id_mid, Iq_mid, params, T_motor);
      const V_d_mid   = -omega_e * sat_mid.Lq * Iq_mid;
      const V_q_mid   = omega_e * (sat_mid.Ld * Id_mid + lpm);
      const V_mag_mid = Math.sqrt(V_d_mid * V_d_mid + V_q_mid * V_q_mid);
      if (V_mag_mid > V_max_fw) Id_lo = Id_mid;
      else { Id_hi = Id_mid; Id_fw_vc = Id_mid; Iq_fw_vc = Iq_mid; }
    }
    // 이탈자속화 보호
    const Id_fw_safe = Math.max(-getDemagLimit(T_motor, I_max, params), Id_fw_vc);
    const Iq_fw_safe = Math.sqrt(Math.max(0, I_max * I_max - Id_fw_safe * Id_fw_safe));
    return { Id: Id_fw_safe, Iq: Iq_fw_safe, regime: 'field_weakening_vc' };
  }
  return { Id: Id_mtpa, Iq: Math.min(Iq_mtpa, I_max), regime: 'current_limited' };
}

// ── computeDeadtimeError ──
/**
 * 인버터 데드타임으로 인한 상전압 오차 산출
 *
 * IGBT 상하암 단락 방지를 위한 데드타임(t_dead)에서 발생하는 전압 오차:
 *   V_dead = sign(I_phase) × (V_dc × t_dead / T_sw + V_f + R_sw × |I|)
 *   t_dead : 데드타임 [s] (기본 2.5 µs)
 *   T_sw   : 스위칭 주기 = 1/f_sw [s]
 *   V_f    : 다이오드 순방향 전압 [V] (기본 0.8)
 *   R_sw   : 스위치 등가 저항 [Ω] (기본 0.002)
 * deadtimeCompensation()에서 dq 변환을 통해 보상 전압 계산에 사용된다.
 *
 * @param {number} I_phase - 해당 상 전류 [A]
 * @param {number} V_dc    - DC 링크 전압 [V]
 * @param {object} params  - 차량 파라미터 (dead_time, carrier_freq, V_f_diode, R_sw)
 * @returns {number}       - 데드타임 전압 오차 [V]
 */
function computeDeadtimeError(I_phase, V_dc, params) {
  const t_dead = params.dead_time || 2.5e-6;
  const T_sw = 1 / (params.carrier_freq || 10000);
  const V_f = params.V_f_diode || 0.8;
  const R_sw = params.R_sw || 0.002;
  
  // 전류 방향 결정
  const sign_I = Math.sign(I_phase);
  
  // 데드타임 전압 오차
  const V_dead = sign_I * (V_dc * t_dead / T_sw + V_f + R_sw * Math.abs(I_phase));
  
  return V_dead;
}

// ── deadtimeCompensation ──
/**
 * dq축 데드타임 보상 전압 계산
 *
 * dq 전류를 abc로 역변환해 각 상의 데드타임 오차를 계산한 후
 * 다시 dq로 변환해 보상 전압(V_d_comp, V_q_comp)을 반환한다.
 * deadtime_compensation_enable=false이면 (0, 0) 반환.
 *
 * @param {number} Id       - d축 전류 [A]
 * @param {number} Iq       - q축 전류 [A]
 * @param {number} theta_e  - 전기 로터 각도 [rad]
 * @param {object} params   - 차량 파라미터
 * @returns {{ V_d_comp: number, V_q_comp: number }} - dq 보상 전압 [V]
 */
function deadtimeCompensation(Id, Iq, theta_e, params) {
  if (!params.deadtime_compensation_enable) {
    return { V_d_comp: 0, V_q_comp: 0 };
  }
  
  // dq → abc 변환으로 상전류 추정
  const I_a = Id * Math.cos(theta_e) - Iq * Math.sin(theta_e);
  const I_b = Id * Math.cos(theta_e - 2*Math.PI/3) - Iq * Math.sin(theta_e - 2*Math.PI/3);
  const I_c = Id * Math.cos(theta_e + 2*Math.PI/3) - Iq * Math.sin(theta_e + 2*Math.PI/3);
  
  // 각 상의 데드타임 오차
  const V_dead_a = computeDeadtimeError(I_a, params.V_dc, params);
  const V_dead_b = computeDeadtimeError(I_b, params.V_dc, params);
  const V_dead_c = computeDeadtimeError(I_c, params.V_dc, params);
  
  // abc → dq 변환
  const V_d_dead = (2/3) * (V_dead_a * Math.cos(theta_e) + 
                             V_dead_b * Math.cos(theta_e - 2*Math.PI/3) + 
                             V_dead_c * Math.cos(theta_e + 2*Math.PI/3));
  
  const V_q_dead = (2/3) * (-V_dead_a * Math.sin(theta_e) - 
                             V_dead_b * Math.sin(theta_e - 2*Math.PI/3) - 
                             V_dead_c * Math.sin(theta_e + 2*Math.PI/3));
  
  return { V_d_comp: -V_d_dead, V_q_comp: -V_q_dead };
}

// ── steinmetzIronLoss_v3 ──
/**
 * Steinmetz 철손 계산 v3 — M270-50A 단위 기반
 *
 * 수정된 Steinmetz 방정식으로 히스테리시스 + 와전류 손실을 계산한다:
 *   P_hys  = k_h × f × B^n × V_iron
 *   P_eddy = k_e × (f × B)² × V_iron
 *   P_iron = P_hys + P_eddy
 * M270-50A 규소강판 기준으로 k_h=100.0, k_e=1.0 기본값 설정.
 * omega_e가 NaN이면 0을 반환한다(poles 미정의 안전 폴백).
 *
 * @param {number} omega_e - 전기각속도 [rad/s]
 * @param {object} params
 *   params.steinmetz_kh  - 히스테리시스 계수 k_h [W/(m³·Hz·T^n)]
 *   params.steinmetz_kc  - 와전류 계수 k_e [W/(m³·Hz²·T²)]
 *   params.steinmetz_n   - 히스테리시스 멱지수 n (기본 1.8)
 *   params.B_peak_ref    - 기준 자속밀도 [T] (기본 1.2)
 *   params.V_iron        - 고정자 철심 부피 [m³] (기본 0.003)
 * @returns {{ P_iron_v3, P_hys, P_eddy, f_elec, B_used }}
 */
function steinmetzIronLoss_v3(omega_e, params) {
  // P_iron = k_h · f · B^n + k_e · (f · B)²
  // f = ω_e / (2π),  B estimated from flux linkage
  // 단위: k_h [W/(m³·Hz·T^n)], k_e [W/(m³·Hz²·T²)], V_iron [m³]
  // [Fix-Steinmetz] 폴백값을 M270-50A 규소강판 기준 단위 일치 값으로 수정
  //   수정 전: kh=0.0025, ke=1.5e-4 → W/kg 혼용으로 30,000~4,500배 과소
  //   수정 후: kh=100.0,  ke=1.0   → V_iron[m³] 곱셈 기준으로 정상 범위
  // [Fix-Poles] omega_e 자체가 NaN이면 f_elec=NaN → P_iron=NaN → 차트에서 0으로 표시됨
  //   원인: params.poles가 DEFAULT_PARAMS에 없어서 차량 캐시 없을 때 undefined
  //   수정: 이미 VEHICLE_PRESETS DEFAULT_PARAMS에 poles:8 추가했으나, 방어 폴백 유지
  if (!omega_e || isNaN(omega_e)) return { P_iron_v3: 0, P_hys: 0, P_eddy: 0, f_elec: 0, B_used: 0 };
  const f_elec = Math.abs(omega_e) / (2 * Math.PI);
  const kh = params.steinmetz_kh ?? 100.0;
  const ke = params.steinmetz_kc ?? 1.0;
  const n  = params.steinmetz_n  ?? 1.8;
  const B  = params.B_peak_ref   ?? 1.2; // assumed peak flux density
  // Volume of motor stator iron (approximate for scaling)
  const V_iron = params.V_iron   ?? 0.003; // [m³] stator iron volume
  const P_hys  = kh * f_elec * Math.pow(B, n) * V_iron;
  const P_eddy = ke * Math.pow(f_elec * B, 2)  * V_iron;
  const P_iron_total = P_hys + P_eddy;
  return { P_iron_v3: P_iron_total, P_hys, P_eddy, f_elec, B_used: B };
}

// ── computeInverterLosses ──
/**
 * 인버터 손실 계산 (도통 + 스위칭 + 보조 전력)
 *
 * 3상 인버터(6개 스위치) 손실 모델:
 *   P_cond = 6 × (V_f × I_rms × 0.45 + R_sw × I_rms²) × f_T × K_cond
 *     0.45: 도통 파워 팩터 계수 (3상 정현파 기준)
 *   P_sw   = 6 × E_on_off × f_sw × (I_rms/300) × (V_dc/400) × f_T × K_sw
 *     f_sw: carrier_freq 우선 → f_sw → 10000 Hz 순 폴백
 *   f_T   = 1 + k_inv_temp × (T_inv − T_ref)  (온도 보정)
 *
 * @param {number} I_rms   - 위상 전류 실효값 [A]
 * @param {number} T_inv   - 인버터 온도 [°C]
 * @param {object} params  - 차량 파라미터 (k_inv_temp, V_f_diode, R_sw, E_on_off 등)
 * @returns {{ P_cond, P_sw, P_aux, P_total }}  - 인버터 손실 [W]
 */
function computeInverterLosses(I_rms, T_inv, params) {
  const tf = 1 + params.k_inv_temp * (T_inv - params.T_ref);
  const Kc = params.K_inv_cond || 1.0, Ks = params.K_inv_sw || 1.0;
  const P_cond = 6 * (params.V_f_diode * I_rms * 0.45 + params.R_sw * I_rms * I_rms) * tf * Kc;
  // [Fix-WARN-2] 스위칭 주파수 파라미터 통합
  //   수정 전: params.f_sw 단독 사용 — deadtimeCompensation/computeDeadtimeError의
  //            params.carrier_freq와 동일 물리량이 두 키로 분산
  //            → VEHICLE_PRESETS에서 carrier_freq만 정의하고 f_sw를 생략하면
  //              P_sw = 6 * E_on_off * undefined * (...) = NaN (묵묵한 손실 0 처리)
  //   수정 후: carrier_freq → f_sw → 10000 Hz 순서로 폴백
  //     · carrier_freq: VEHICLE_PRESETS 표준 키 (deadtimeCompensation과 공유)
  //     · f_sw: 구형 파라미터 키 (하위 호환 유지)
  //     · 10000: 안전 기본값 (10 kHz — Si IGBT 일반 범위)
  //   VEHICLE_PRESETS에 carrier_freq와 f_sw가 동시 정의된 경우 carrier_freq 우선
  const f_sw_eff = params.carrier_freq ?? params.f_sw ?? 10000;
  const P_sw = 6 * params.E_on_off * f_sw_eff * (I_rms / 300) * (params.V_dc / 400) * tf * Ks;
  const P_aux = params.inv_aux_power;
  return { P_cond, P_sw, P_aux, P_total: P_cond + P_sw + P_aux };
}

// ── computeMotorLosses ──
/**
 * 통합 모터 손실 계산
 *
 * 개선 항목 (이번 업데이트):
 *   [Fix-ACSkin]  AC 동손: omega_e → Rs_T에 주입 → 표피효과 반영
 *   [Fix-PMEddy]  PM 와전류 손실: NdFeB 영구자석 내 와전류 항 추가
 *   [Fix-KhTemp]  Steinmetz k_h 온도 의존성: 고온→k_h 감소, 저온→증가
 *   [Fix-BearT]   베어링 손실 온도 의존성: 저온 그리스 점도 → 기계 손실 상승
 */
function computeMotorLosses(omega, T_motor_torque, Id, Iq, params, T_motor) {
  const omega_e = omega * (params.poles / 2);
  const f_e     = Math.abs(omega_e) / (2 * Math.PI);
  const T_mot   = T_motor ?? 25;
  const T_ref   = params.T_ref || 25;

  // ── [Fix-ACSkin] AC 동손: omega_e → Rs_T에 전달 ──────────────────────────
  // skin_effect_enable=true 시 Rs_T가 k_ac 계수를 곱해 반환
  const _p_with_omega = params.skin_effect_enable
    ? { ...params, omega_e_for_Rs: omega_e }
    : params;
  const Rs    = Rs_T(T_mot, _p_with_omega);
  const I_rms = Math.sqrt(Id * Id + Iq * Iq);
  const P_cu  = 1.5 * Rs * I_rms * I_rms * (params.K_cu || 1.0);

  const lpm   = lambda_pm_T(T_mot, params);
  const B_est = Math.min(1.5, lpm * 2);

  // ── [Fix-KhTemp] Steinmetz k_h 온도 의존성 ────────────────────────────────
  // 히스테리시스 손실계수 k_h는 규소강판 결정 배향이 온도에 따라 변함
  //   경험식: k_h(T) = k_h0 × max(0.6, 1 − k_h_T × (T − T_ref))
  //   k_h_T ≈ 0.002 /°C  (방향성 전기강판 M270-35A 기준, Boglietti 2003)
  //   저온: k_h 소폭 증가 (저온 자기장 변화 지연)
  let P_fe;
  if (params.steinmetz_enable) {
    const kh_base  = params.steinmetz_kh ?? 100.0;
    const ke_base  = params.steinmetz_kc ?? 1.0;
    const n_sz     = params.steinmetz_n  ?? 1.8;
    const B_sz     = params.B_peak_ref   ?? 1.2;
    const V_iron   = params.V_iron       ?? 0.003;
    const k_h_T    = params.steinmetz_k_h_T ?? 0.0020;  // k_h 온도계수 [/°C]
    const f_kh_T   = Math.max(0.60, 1 - k_h_T * (T_mot - T_ref));  // 온도 보정 (고온→감소)
    const kh_eff   = kh_base * f_kh_T;
    const P_hys    = kh_eff * f_e * Math.pow(Math.max(B_sz, 0.01), n_sz) * V_iron;
    const P_eddy   = ke_base * Math.pow(f_e * B_sz, 2) * V_iron;
    P_fe = (P_hys + P_eddy) * (params.K_fe || 1.0);
  } else {
    const k_h_T    = params.steinmetz_k_h_T ?? 0.0020;
    const f_kh_T   = Math.max(0.60, 1 - k_h_T * (T_mot - T_ref));
    const kh_simple = params.k_h * f_kh_T;
    P_fe = (kh_simple * f_e * Math.pow(Math.max(B_est, 0.01), 1.6)
           + params.k_e * f_e * f_e * B_est * B_est) * 50 * (params.K_fe || 1.0);
  }

  // ── [Fix-PMEddy] 영구자석 와전류 손실 ────────────────────────────────────
  // NdFeB 자석 내 고주파 플럭스 변동으로 인한 와전류 손실
  //   P_pm_eddy = k_pm_e × f_e² × B_pm² × V_pm
  //   k_pm_e : NdFeB 와전류 계수 [W/(m³·Hz²·T²)] (기본 0.30 — 분할 자석 기준)
  //   B_pm   : 자석 내 플럭스 밀도 [T] (λpm 기반 추정)
  //   V_pm   : 영구자석 총 부피 [m³] (기본 0.0005 m³ ≈ 500 cm³, 일반 EV 모터)
  // 참고: Islam et al. (2010), "Permanent-magnet …losses in …IPMSM"
  let P_pm_eddy = 0;
  if (params.pm_eddy_enable !== false) {  // 기본 활성 (opt-out 방식)
    const k_pm_e = params.k_pm_eddy   ?? 0.30;   // [W/(m³·Hz²·T²)]
    const V_pm   = params.V_pm        ?? 5e-4;   // [m³] NdFeB 자석 부피
    const B_pm   = Math.min(1.5, lpm * 1.8);     // 자석 내 플럭스 밀도 추정
    P_pm_eddy    = k_pm_e * f_e * f_e * B_pm * B_pm * V_pm * (params.K_pm_eddy || 1.0);
  }

  // ── [Fix-BearT] 베어링 손실 온도 의존성 ──────────────────────────────────
  // 저온 시 그리스 점도 상승 → 베어링 마찰 손실 증가
  //   P_mech(T) = P_mech_nom × f_bear(T)
  //   f_bear = 1 + k_bear_cold × max(0, T_bear_ref − T_motor)
  //   k_bear_cold ≈ 0.012 /°C  (SKF 베어링 공학 데이터 기반)
  //   T_bear_ref  = 40°C (베어링 정상 운전 기준 온도)
  // 고온 영역: 점도 감소로 손실 미미하게 줄지만 수명 영향 → 손실은 1.0 유지
  const k_bear_cold  = params.k_bear_cold  ?? 0.012;  // [/°C]
  const T_bear_ref   = params.T_bear_ref   ?? 40;     // [°C]
  const f_bear_T     = 1 + k_bear_cold * Math.max(0, T_bear_ref - T_mot);
  // 기존 고온 보정(tf_m)과 통합: 두 효과를 분리해서 합산
  const f_mech_hot   = T_mot < 0 ? 1.3 : T_mot < 10 ? 1.15 : 1.0; // 저온 고점도 (기존 로직 유지)
  const P_mech_base  = (params.k_m0 + params.k_m1 * omega + params.k_m2 * omega * omega) * (params.K_mech || 1.0);
  // f_bear_T와 f_mech_hot 중 큰 값을 취함 (저온에서 두 효과 중복 적용 방지)
  const f_mech_total = Math.max(f_mech_hot, f_bear_T);
  const P_mech       = P_mech_base * f_mech_total;

  const P_total = P_cu + P_fe + P_pm_eddy + P_mech;
  return {
    P_cu, P_fe, P_pm_eddy, P_mech, P_total,
    Rs_eff: Rs, lambda_eff: lpm,
    // 세부 진단 필드
    f_bear_T: +f_bear_T.toFixed(3),
    f_kh_T:   +(Math.max(0.60, 1 - (params.steinmetz_k_h_T ?? 0.0020) * (T_mot - T_ref))).toFixed(3),
  };
}

// ── getLUTPkScale ──
/**
 * 드라이브 모드별 Traction LUT 피크 토크 스케일 반환
 *
 * traction_lut_enable=true이면 해당 모드의 LUT 마지막 행 최댓값을 T_max_motor로 나누어
 * 정규화 스케일을 계산한다. LUT가 없으면 모드별 torque_ratio 파라미터를 반환한다.
 *
 * @param {object} params - 차량 파라미터
 * @param {string} [mode] - 드라이브 모드 ('ECO'|'NORMAL'|'SPORT'|'SNOW'|'OFF_ROAD')
 * @returns {number}      - 토크 스케일 [0, ~1.15]
 */
function getLUTPkScale(params, mode) {
  // [Fix-M1] var → const/let: 파일 내 스타일 일관성 통일
  const _mode = mode || (params && params.drive_mode) || 'NORMAL';
  if (params && params.traction_lut_enable) {
    const lutKey = { ECO:'traction_lut_eco', NORMAL:'traction_lut_normal',
      SPORT:'traction_lut_sport', SNOW:'traction_lut_snow',
      OFF_ROAD:'traction_lut_offroad' }[_mode] || 'traction_lut_normal';
    const lut = params[lutKey];
    if (lut && lut.length >= 2) {
      const lastRow = lut[lut.length - 1];
      const lutMax = Math.max.apply(null, lastRow);
      const T_max = params.T_max_motor || 400;
      return Math.max(0.1, lutMax / T_max); // APP=100% 피크 / T_max_motor
    }
  }
  return ({ ECO: params.eco_max_torque_ratio||0.7,
            NORMAL: params.normal_max_torque_ratio||1.0,
            SPORT: params.sport_max_torque_ratio||1.15,
            SNOW: params.snow_max_torque_ratio||0.55,
            OFF_ROAD: params.offroad_max_torque_ratio||1.0 })[_mode] || 1.0;
}

// ── validateTractionLUT ──
/**
 * Traction LUT 유효성 검사 (5가지 규칙 적용)
 *
 * 검사 항목:
 *   ① APP=0% 행의 모든 값이 0인지 (공전 토크 없어야 함)
 *   ② APP=100%, v=0 셀이 양수인지 (최대 정지 토크 정의 필수)
 *   ③ 행 단조 감소 (속도 증가 → 토크 감소 또는 유지)
 *   ④ 열 단조 증가 (APP% 증가 → 토크 증가)
 *   ⑤ 최댓값이 T_max_motor × 1.5 이하인지
 *
 * @param {number[][]} lut        - 2D Traction LUT [APP%][속도 열]
 * @param {number}     T_max_motor - 모터 최대 토크 [Nm]
 * @returns {{ valid: boolean, errors: string[], warnings: string[] }}
 */
function validateTractionLUT(lut, T_max_motor) {
  // [Fix-M1] var → const/let: 파일 내 스타일 일관성 통일
  const errors = [], warnings = [];
  if (!lut || lut.length < 2) { return { valid: false, errors: ['LUT가 비어 있습니다'], warnings: [] }; }
  const T_max = T_max_motor || 400;
  // ① APP=0% 행 전부 0이어야 함
  if (lut[0].some(v => v !== 0)) {
    warnings.push('APP=0% 행에 0이 아닌 값 — 페달 미입력 시 토크 발생');
  }
  // ② APP=100%, v=0 셀 > 0
  if (!lut[lut.length-1][0] || lut[lut.length-1][0] <= 0) {
    errors.push('APP=100%, v=0 셀이 0 — 최대 토크가 정의되지 않았습니다. LUT를 재설정하세요');
  }
  // ③ 행 단조 감소 검사 (v↑ → T↓)
  const rowViol = [];
  lut.forEach((row, ri) => {
    for (let ci = 0; ci < row.length - 1; ci++) {
      if (row[ci+1] > row[ci] + 0.5) rowViol.push('APP=' + (ri===0?0:ri*10>=100?100:ri) + '%, v열 ' + ci + '→' + (ci+1));
    }
  });
  if (rowViol.length) warnings.push('속도 증가 시 토크 증가 셀: ' + rowViol.slice(0,3).join(', '));
  // ④ 열 단조 증가 검사 (APP%↑ → T↑)
  const colViol = [];
  for (let ci = 0; ci < (lut[0]||[]).length; ci++) {
    for (let ri = 0; ri < lut.length - 1; ri++) {
      if ((lut[ri+1][ci]||0) < (lut[ri][ci]||0) - 0.5) colViol.push('v열' + ci + ', APP행 ' + ri + '→' + (ri+1));
    }
  }
  if (colViol.length) warnings.push('APP% 감소 시 토크 증가 셀: ' + colViol.slice(0,3).join(', '));
  // ⑤ 최대값 ≤ T_max × 1.5
  const maxVal = Math.max(...lut.map(r => Math.max(...r)));
  if (maxVal > T_max * 1.5) {
    errors.push('최대 토크 ' + maxVal.toFixed(0) + 'N·m이 모터 정격의 150%(' + (T_max*1.5).toFixed(0) + 'N·m) 초과 — 물리적으로 불가능');
  } else if (maxVal > T_max * 1.2) {
    warnings.push('최대 토크 ' + maxVal.toFixed(0) + 'N·m이 정격의 120% 초과 — 과부스트 확인 필요');
  }
  return { valid: errors.length === 0, errors: errors, warnings: warnings };
}

// ── backEMFObserver ──
/**
 * Back-EMF 기반 센서리스 위치/속도 추정 (고속용)
 *
 * IPMSM 전압 방정식에서 Back-EMF를 역산하고 PLL로 로터 위치를 추정한다:
 *   E_d_meas = Vd − Rs·Id − Ld·dId/dt
 *   E_q_meas = Vq − Rs·Iq − Lq·dIq/dt
 *   Δθ = atan2(E_q_error, E_d_error)
 *   ω_est += (Kp·Δθ + Ki·∫Δθ dt) · dt
 *
 * 유효 속도 범위: |ωe| > 50 rad/s (저속에서는 HFI 사용)
 * 첫 호출 시 Id_prev/Iq_prev 미정의 → 현재값으로 초기화 (과도 오차 방지).
 *
 * @param {number} Id       - d축 전류 [A]
 * @param {number} Iq       - q축 전류 [A]
 * @param {number} Vd       - d축 전압 [V]
 * @param {number} Vq       - q축 전압 [V]
 * @param {number} omega_m  - 기계각속도 [rad/s]
 * @param {object} params   - 차량 파라미터 (Kp_pll, Ki_pll 등)
 * @param {number} dt       - 타임스텝 [s]
 * @param {object} state    - 센서리스 내부 상태 (initSensorlessState 반환)
 * @returns {object}        - 갱신된 state (theta_est, omega_est, confidence)
 */
function backEMFObserver(Id, Iq, Vd, Vq, omega_m, params, dt, state) {
  const omega_e = omega_m * (params.poles / 2);
  const Rs = Rs_T(state.T_motor || 25, params);
  const lpm = lambda_pm_T(state.T_motor || 25, params);
  
  // 추정된 백-EMF
  const E_d_est = -state.omega_est * params.Lq * Iq;
  const E_q_est = state.omega_est * (params.Ld * Id + lpm);
  
  // 실제 측정 백-EMF (전압 방정식 역산)
  // [Fix-BEMF-Init] 첫 호출(Id_prev 미정의) 시 현재값으로 초기화 → dId/dt = 0
  //   기존: state.Id_prev || 0 → 초기 스텝에서 수백 A/s 과도 오차 발생 가능
  if (state.Id_prev === undefined) state.Id_prev = Id;
  if (state.Iq_prev === undefined) state.Iq_prev = Iq;
  const dId = (Id - state.Id_prev) / dt;
  const dIq = (Iq - state.Iq_prev) / dt;
  // [A21-18-03] B-03 수정: IPMSM 전압방정식 역산
  // V_d = Rs·Id + Ld·dId/dt - ωe·Lq·Iq  →  E_d_meas = Vd - Rs·Id - Ld·dId
  // V_q = Rs·Iq + Lq·dIq/dt + ωe·Ld·Id  →  E_q_meas = Vq - Rs·Iq - Lq·dIq
  // 기존 오류: d축에 Lq·dIq, q축에 Ld·dId 교차 사용 → 수정
  const E_d_meas = Vd - Rs * Id - params.Ld * dId; // d축: Ld·dId 사용
  const E_q_meas = Vq - Rs * Iq - params.Lq * dIq; // q축: Lq·dIq 사용
  
  // 오차
  const E_error_d = E_d_meas - E_d_est;
  const E_error_q = E_q_meas - E_q_est;
  
  // PLL (Phase-Locked Loop)
  const Kp_pll = params.Kp_pll || 100;
  const Ki_pll = params.Ki_pll || 5000;
  
  // 오차를 각도 오차로 변환
  const theta_error = Math.atan2(E_error_q, E_error_d);
  
  // PI 제어
  state.error_integral += theta_error * dt;
  const omega_correction = Kp_pll * theta_error + Ki_pll * state.error_integral;
  
  // [Fix-BEMF-1] 속도 업데이트: 기존 omega_e fallback은 관측기를 무효화
  //   수정: omega_correction로 PLL 기반 독립 추정; 저속 초기화만 omega_e 사용
  if (Math.abs(state.omega_est) < 5 && Math.abs(omega_e) > 5) {
    state.omega_est = omega_e;  // 저속/정지에서 초기값만 동기화
  } else {
    state.omega_est += omega_correction * dt;  // [Fix-BEMF-1] PLL 독립 추정
  }
  
  // 위치 업데이트
  state.theta_est = state.theta_est + state.omega_est * dt;
  state.theta_est = state.theta_est % (2 * Math.PI);
  
  // 이전 전류 저장
  state.Id_prev = Id;
  state.Iq_prev = Iq;
  
  // 신뢰도 (속도 기반)
  state.confidence = Math.abs(omega_e) > 50 ? Math.min(1.0, Math.abs(omega_e) / 200) : 0.3;
  state.method = 'back_emf';
  
  return state;
}

// ── hfiSensorlessStep ──
/**
 * 저속 HFI (High Frequency Injection) 센서리스 위치 추정
 *
 * 동작 원리:
 *   고주파 전압 Vh·cos(ωh·t) 를 d축에 주입
 *   → IPMSM 돌극비(Ld≠Lq)로 인해 q축에 전류 응답 발생
 *   → 응답 전류에서 로터 위치 오차 Δθ 추출
 *   → PLL로 위치/속도 추정
 *
 * 유효 속도 범위: |ωe| < ωe_hfi_max (기본 50 rad/s ≈ 5 km/h)
 *
 * @param {number} Id          - d축 전류 [A]
 * @param {number} Iq          - q축 전류 [A]
 * @param {number} omega_e     - 전기각속도 [rad/s]
 * @param {object} params      - 차량 파라미터
 * @param {number} dt          - 시간 스텝 [s]
 * @param {object} state       - HFI 내부 상태 (initSensorlessState 반환값)
 * @returns {object}           - 갱신된 state (theta_est, omega_est, confidence, method)
 */
function hfiSensorlessStep(Id, Iq, omega_e, params, dt, state) {
  // ── HFI 파라미터 ─────────────────────────────────────────────
  const Vh      = params.hfi_voltage     || 20;    // 주입 전압 진폭 [V]
  const f_hfi   = params.hfi_freq        || 500;   // 주입 주파수 [Hz]
  const omega_h = 2 * Math.PI * f_hfi;
  const Kp_hfi  = params.Kp_hfi         || 200;   // PLL 비례 게인
  const Ki_hfi  = params.Ki_hfi         || 2000;  // PLL 적분 게인
  const Ld      = params.Ld             || 0.0003;
  const Lq      = params.Lq             || 0.0006;
  const dL      = Lq - Ld;                          // 돌극차 (> 0 for IPMSM)

  // ── HFI 상태 초기화 (없으면) ──────────────────────────────────
  if (!state.hfi_t)            state.hfi_t           = 0;
  if (!state.hfi_err_integral) state.hfi_err_integral = 0;
  if (!state.hfi_Iq_prev)      state.hfi_Iq_prev      = 0;
  if (!state.hfi_Iq_filt)      state.hfi_Iq_filt      = 0;

  // ── 주입 전압 (d축) ──────────────────────────────────────────
  // V_d_inject = Vh · cos(ωh · t)
  state.hfi_t += dt;
  const V_inject = Vh * Math.cos(omega_h * state.hfi_t);

  // ── 전류 응답 추출 ────────────────────────────────────────────
  // 이론: 고주파 q축 전류 응답 Iq_hf ≈ (Vh·ΔL)/(2·Ld·Lq·ωh) · sin(2·Δθ)
  // Δθ가 작으면: Iq_hf ≈ (Vh·ΔL)/(Ld·Lq·ωh) · Δθ  → 오차 신호
  const dIq = Iq - state.hfi_Iq_prev;
  state.hfi_Iq_prev = Iq;

  // ── 복조 (Demodulation): 고주파 성분 추출 ────────────────────
  // sin(ωh·t)와 곱하고 LPF로 저주파 성분(위치 오차) 추출
  const demod = dIq * Math.sin(omega_h * state.hfi_t);
  // 1차 LPF (τ = 1/(2π·50Hz) ≈ 0.003s)
  const tau_lpf = params.hfi_lpf_tau || 0.003;
  const alpha   = dt / (tau_lpf + dt);
  state.hfi_Iq_filt = state.hfi_Iq_filt + alpha * (demod - state.hfi_Iq_filt);

  // ── 위치 오차 신호 ────────────────────────────────────────────
  // dL > 0 이면 오차 신호 부호가 올바름
  const theta_err = dL > 1e-6
    ? state.hfi_Iq_filt / (Math.max(1e-6, dL) * Vh * omega_h + 1e-9)
    : 0;

  // ── PLL: 오차 → 위치/속도 추정 ───────────────────────────────
  state.hfi_err_integral += theta_err * dt;
  state.hfi_err_integral  = Math.max(-Math.PI, Math.min(Math.PI, state.hfi_err_integral));
  const omega_corr = Kp_hfi * theta_err + Ki_hfi * state.hfi_err_integral;

  state.omega_est = omega_corr;
  state.theta_est = ((state.theta_est || 0) + state.omega_est * dt) % (2 * Math.PI);

  // ── 신뢰도: 저속일수록 높음, 고속에서 낮아짐 ─────────────────
  const omega_max_hfi = params.hfi_omega_max || 50;  // [rad/s]
  state.confidence = Math.max(0, 1 - Math.abs(omega_e) / omega_max_hfi);
  state.method     = 'hfi';
  state.V_inject   = V_inject;  // 출력에 포함 (디버그용)

  return state;
}

// ── sensorlessObserver ──
/**
 * 통합 센서리스 관측기 — HFI(저속) / Back-EMF(고속) 자동 전환
 *
 * 전환 히스테리시스:
 *   |ωe| < ωe_hfi_max × 0.7  → HFI 전용
 *   |ωe| > ωe_hfi_max × 1.3  → Back-EMF 전용
 *   중간 구간                  → 가중 혼합 (블렌딩)
 */
function sensorlessObserver(Id, Iq, Vd, Vq, omega_m, params, dt, state) {
  const omega_e     = omega_m * (params.poles / 2);
  const omega_max_hfi = params.hfi_omega_max || 50;
  const hfi_enable  = params.hfi_enable !== false && Math.abs(params.Ld - params.Lq) > 1e-6;

  // ── HFI 전용 구간 ─────────────────────────────────────────────
  if (hfi_enable && Math.abs(omega_e) < omega_max_hfi * 0.7) {
    return hfiSensorlessStep(Id, Iq, omega_e, params, dt, state);
  }

  // ── Back-EMF 전용 구간 ────────────────────────────────────────
  if (!hfi_enable || Math.abs(omega_e) >= omega_max_hfi * 1.3) {
    return backEMFObserver(Id, Iq, Vd, Vq, omega_m, params, dt, state);
  }

  // ── 혼합 구간 (블렌딩) ────────────────────────────────────────
  // Back-EMF 결과와 HFI 결과를 속도에 비례해 혼합
  const hfi_state  = { ...state };
  const bemf_state = { ...state };
  const hfi_out    = hfiSensorlessStep(Id, Iq, omega_e, params, dt, hfi_state);
  const bemf_out   = backEMFObserver(Id, Iq, Vd, Vq, omega_m, params, dt, bemf_state);

  // 블렌딩 가중치: ω 높을수록 BEMF 비중 증가
  const w_bemf = (Math.abs(omega_e) - omega_max_hfi * 0.7) / (omega_max_hfi * 0.6);
  const w_hfi  = 1 - w_bemf;

  state.theta_est   = hfi_out.theta_est  * w_hfi + bemf_out.theta_est  * w_bemf;
  state.omega_est   = hfi_out.omega_est  * w_hfi + bemf_out.omega_est  * w_bemf;
  state.confidence  = hfi_out.confidence * w_hfi + bemf_out.confidence * w_bemf;
  state.method      = 'blended';
  // 두 관측기 상태를 병합해 다음 스텝에서 재사용
  Object.assign(state, {
    Id_prev: bemf_out.Id_prev, Iq_prev: bemf_out.Iq_prev,
    error_integral: bemf_out.error_integral,
    hfi_t: hfi_out.hfi_t, hfi_Iq_prev: hfi_out.hfi_Iq_prev,
    hfi_Iq_filt: hfi_out.hfi_Iq_filt, hfi_err_integral: hfi_out.hfi_err_integral,
  });
  return state;
}

// ── mrasObserver ── (Model Reference Adaptive System, Popov 초안정성)
/**
 * MRAS 기반 센서리스 속도/위치 추정 — αβ 정지 좌표계
 *
 * ■ 원리 (Schauder-type MRAS, Popov의 초안정성 정리 기반)
 *   참조 모델 (Reference Model) — 전압 방정식 기반 고정자 자속:
 *     ψ_α_ref = ∫(V_α − Rs·I_α) dt        (전압 모델, 직접 적분)
 *     ψ_β_ref = ∫(V_β − Rs·I_β) dt
 *   조정 모델 (Adjustable Model) — 추정 회전각 θ̂ 기반:
 *     ψ_α_adj = Ld·I_α + λpm·cos(θ̂)      (전류 모델)
 *     ψ_β_adj = Lq·I_β + λpm·sin(θ̂)
 *   오차 신호 ε (Popov 교차적):
 *     ε = ψ_α_ref·ψ_β_adj − ψ_β_ref·ψ_α_adj
 *   적응 법칙 (PI):
 *     ω̂ = Kp_mras·ε + Ki_mras·∫ε dt
 *     θ̂ = ∫ω̂ dt
 *
 * ■ 전압 모델 직류 드리프트 억제
 *   순수 적분은 DC 오프셋 누적 발산 문제가 있으므로
 *   1차 LPF–HPF 결합 보상(low-pass with cutoff ω_c)을 적용:
 *     dψ/dt = (V − Rs·I) − ω_c·ψ
 *   ω_c = params.mras_lp_cutoff || 2π × 1 Hz  (기본 1 Hz)
 *
 * ■ 유효 범위: 중·고속 (|ωe| > ωe_mras_min, 기본 30 rad/s)
 *   저속에서는 전압 모델 정확도 저하 → HFI 또는 BEMF 관측기와 병용할 것
 *
 * @param {number} I_alpha    - α축 고정자 전류 [A]
 * @param {number} I_beta     - β축 고정자 전류 [A]
 * @param {number} V_alpha    - α축 고정자 전압 [V]
 * @param {number} V_beta     - β축 고정자 전압 [V]
 * @param {object} params     - 차량 파라미터
 * @param {number} dt         - 시간 스텝 [s]
 * @param {object} state      - MRAS 내부 상태 (initSensorlessState 반환값)
 * @returns {object}          - 갱신된 state (theta_est, omega_est, confidence, method)
 *
 * 참고: Schauder & Weder (1995), "A new speed adaptive flux observer for induction …";
 *       Piippo et al. (2008) IPMSM MRAS; Zaky et al. (2013) flux-linkage MRAS
 */
function mrasObserver(I_alpha, I_beta, V_alpha, V_beta, params, dt, state) {
  const Rs  = Rs_T(state.T_motor || 25, params);
  const lpm = lambda_pm_T(state.T_motor || 25, params);
  const Ld  = params.Ld || 3e-4;
  const Lq  = params.Lq || 6e-4;

  // ── 참조 모델: 전압 적분 (LP 보상으로 DC 드리프트 억제) ──────────────
  const omega_c = params.mras_lp_cutoff ?? (2 * Math.PI * 1.0); // [rad/s], 기본 1 Hz
  state.mras_psi_alpha_ref = (state.mras_psi_alpha_ref ?? 0)
    + ((V_alpha - Rs * I_alpha) - omega_c * (state.mras_psi_alpha_ref ?? 0)) * dt;
  state.mras_psi_beta_ref  = (state.mras_psi_beta_ref ?? 0)
    + ((V_beta  - Rs * I_beta)  - omega_c * (state.mras_psi_beta_ref  ?? 0)) * dt;

  // ── 조정 모델: 전류 모델 (추정 각도 θ̂ 사용) ─────────────────────────
  const theta = state.theta_est ?? 0;
  const psi_alpha_adj = Ld * I_alpha + lpm * Math.cos(theta);
  const psi_beta_adj  = Lq * I_beta  + lpm * Math.sin(theta);

  // ── 오차 신호 (Popov 교차적, 부호: ω̂↑ → ε→0) ──────────────────────
  const epsilon = state.mras_psi_alpha_ref * psi_beta_adj
                - state.mras_psi_beta_ref  * psi_alpha_adj;

  // ── PI 적응 법칙 ───────────────────────────────────────────────────────
  const Kp_mras = params.Kp_mras ?? 50;
  const Ki_mras = params.Ki_mras ?? 1200;
  state.mras_integral  = (state.mras_integral ?? 0) + epsilon * dt;
  // 적분 와인드업 방지
  const omega_lim = (params.omega_e_max ?? 3000);
  state.mras_integral  = Math.max(-omega_lim / Ki_mras, Math.min(omega_lim / Ki_mras, state.mras_integral));

  state.omega_est  = Kp_mras * epsilon + Ki_mras * state.mras_integral;
  state.theta_est  = ((state.theta_est ?? 0) + state.omega_est * dt) % (2 * Math.PI);

  // ── 신뢰도: 중속 이상에서 안정 ────────────────────────────────────────
  const omega_min = params.mras_omega_min ?? 30; // [rad/s]
  state.confidence = Math.min(1.0, Math.max(0, (Math.abs(state.omega_est) - omega_min) / omega_min));
  state.method     = 'mras';

  return state;
}

// ── initSensorlessState ──
/**
 * 센서리스 관측기 공통 초기 상태 생성
 *
 * backEMFObserver, hfiSensorlessStep, mrasObserver에서 공유하는
 * 내부 상태 객체를 초기화한다.
 *
 * @param {object} params - 차량 파라미터 (미사용, 확장 대비)
 * @returns {{ theta_est, omega_est, error_integral, Id_prev, Iq_prev, confidence, method,
 *             hfi_t, hfi_err_integral, hfi_Iq_prev, hfi_Iq_filt, V_inject,
 *             mras_psi_alpha_ref, mras_psi_beta_ref, mras_integral }}
 */
function initSensorlessState(params) {
  return {
    theta_est:         0,
    omega_est:         0,
    error_integral:    0,
    Id_prev:           0,
    Iq_prev:           0,
    confidence:        0,
    method:            'startup',
    // HFI 전용 상태
    hfi_t:             0,
    hfi_err_integral:  0,
    hfi_Iq_prev:       0,
    hfi_Iq_filt:       0,
    V_inject:          0,
    // MRAS 전용 상태 (αβ 자속 적분, PI 적분기)
    mras_psi_alpha_ref: 0,
    mras_psi_beta_ref:  0,
    mras_integral:      0,
  };
}

// ── computeTorqueRipple ──
/**
 * [Low-TR] 토크 리플 6고조파 모델 — PWM 고조파 → 토크 맥동 → NVH
 *
 * 기존 `harmonicCompensation`은 전압 고조파를 보상하는 *제어 관점* 함수.
 * 이 함수는 잔류 고조파가 토크에 미치는 *물리 관점* 맥동량을 산출해
 * 시뮬레이션 출력(토크 파형, NVH 분석)에 사용한다.
 *
 * IPMSM 토크 리플 주요 성분:
 *   - 코깅 토크: 슬롯 주기 (f_cog = N_slot × Ω_m / 2π)
 *   - 전류 고조파: 6차 (인버터 6-스텝 특성), 12차, 18차 ... (6k 차)
 *     T_ripple_6k = A_6k × T_e_mean × sin(6k·θ_e + φ_6k)
 *   - cogging:  T_cog = A_cog × T_cog0 × sin(N_slot × θ_mech)
 *
 * 기본값 (일반 EV 모터 기준):
 *   A_6   : 0.02  (2%  — 6차,  인버터 주파수 기인 최대 성분)
 *   A_12  : 0.008 (0.8% — 12차)
 *   A_18  : 0.003 (0.3% — 18차)
 *   A_cog : 0.005 (0.5% × T_cog0 / T_max — 코깅)
 *
 * 파라미터:
 *   params.torque_ripple_enable   : 활성화 (기본 false — opt-in)
 *   params.tr_A6, tr_A12, tr_A18  : 6/12/18차 리플 비율 (0~1)
 *   params.tr_phi6, tr_phi12      : 위상 오프셋 [rad]
 *   params.tr_A_cog               : 코깅 토크 진폭 비율
 *   params.N_slot                 : 스테이터 슬롯 수 (기본 48)
 *   params.T_cog0                 : 코깅 기준 토크 [N·m] (기본 T_max×0.005)
 *
 * @param {number} theta_e    - 전기각 [rad]
 * @param {number} theta_mech - 기계각 [rad]  (= theta_e / (p/2))
 * @param {number} T_e_mean   - 평균 전자기 토크 [N·m]
 * @param {object} params
 * @returns {{ T_ripple, T_6, T_12, T_18, T_cog, ripple_pct }}
 *
 * 참고: Zhu & Howe (2000), "Influence of design parameters on … torque ripple"
 *        IEEE Trans. Energy Convers. 15(4), 407–412
 */
function computeTorqueRipple(theta_e, theta_mech, T_e_mean, params) {
  if (!params.torque_ripple_enable) {
    return { T_ripple: 0, T_6: 0, T_12: 0, T_18: 0, T_cog: 0, ripple_pct: 0 };
  }
  const T_abs  = Math.abs(T_e_mean);
  // 6k 고조파 성분
  const A6  = params.tr_A6   ?? 0.020;
  const A12 = params.tr_A12  ?? 0.008;
  const A18 = params.tr_A18  ?? 0.003;
  const p6  = params.tr_phi6  ?? 0;
  const p12 = params.tr_phi12 ?? (Math.PI / 6);
  const p18 = params.tr_phi18 ?? (Math.PI / 3);
  const T_6  = A6  * T_abs * Math.sin(6  * theta_e + p6);
  const T_12 = A12 * T_abs * Math.sin(12 * theta_e + p12);
  const T_18 = A18 * T_abs * Math.sin(18 * theta_e + p18);

  // 코깅 토크 (기계각 기반)
  const N_slot = params.N_slot  || 48;
  const T_max  = params.T_max_motor || 400;
  const T_cog0 = params.T_cog0  || T_max * 0.005;  // 기본 0.5% T_max
  const A_cog  = params.tr_A_cog ?? 1.0;           // 코깅 진폭 스케일
  const T_cog  = A_cog * T_cog0 * Math.sin(N_slot * theta_mech);

  const T_ripple = T_6 + T_12 + T_18 + T_cog;
  const ripple_pct = T_abs > 1 ? +(Math.abs(T_ripple) / T_abs * 100).toFixed(2) : 0;

  return {
    T_ripple: +T_ripple.toFixed(3),
    T_6:      +T_6.toFixed(3),
    T_12:     +T_12.toFixed(3),
    T_18:     +T_18.toFixed(3),
    T_cog:    +T_cog.toFixed(3),
    ripple_pct,
  };
}

// ── harmonicCompensation ──
/**
 * 전류 고조파 보상 전압 산출 (역위상 주입)
 *
 * 5/7/11/13차 전류 고조파에 역위상 보상 전압을 생성해 전류 왜곡을 억제한다.
 * harmonic_rejection_enable=false이면 (0, 0) 반환.
 *
 * 보상 전압: V_comp_h = −amp × V_dc × [cos(h·θ_e + φ), sin(h·θ_e + φ)]
 *
 * @param {number} theta_e  - 전기각 [rad]
 * @param {number} omega_e  - 전기각속도 [rad/s] (미사용, 확장용)
 * @param {number} Id       - d축 전류 [A]
 * @param {number} Iq       - q축 전류 [A]
 * @param {object} params   - 차량 파라미터 (harmonic_rejection_enable, V_dc)
 * @returns {{ V_d_comp: number, V_q_comp: number }}
 */
function harmonicCompensation(theta_e, omega_e, Id, Iq, params) {
  if (!params.harmonic_rejection_enable) {
    return { V_d_comp: 0, V_q_comp: 0 };
  }
  
  // 주요 고조파: 5차, 7차, 11차, 13차
  const harmonics = [
    { order: 5, amp_d: 0.03, amp_q: 0.05, phase: 0 },
    { order: 7, amp_d: 0.02, amp_q: 0.03, phase: Math.PI/6 },
    { order: 11, amp_d: 0.01, amp_q: 0.015, phase: 0 },
    { order: 13, amp_d: 0.008, amp_q: 0.012, phase: Math.PI/4 }
  ];
  
  let V_d_comp = 0;
  let V_q_comp = 0;
  
  harmonics.forEach(h => {
    const theta_h = h.order * theta_e + h.phase;
    
    // 보상 전압 (역위상)
    V_d_comp += -h.amp_d * params.V_dc * Math.cos(theta_h);
    V_q_comp += -h.amp_q * params.V_dc * Math.sin(theta_h);
  });
  
  return { V_d_comp, V_q_comp };
}

// ── repetitiveController ──
/**
 * 반복 제어기(RC) 1스텝 — 주기적 오차 학습 보상
 *
 * 1주기(T=2π/ωe) 전의 오차를 참조해 학습 게인 Kr로 보상 출력을 생성한다:
 *   u_rep = Q × e[k−N] + Kr × e[k]
 *   Q   : 안정성 필터 (기본 0.98, 1 미만에서 안정 보장)
 *   Kr  : 학습률 (기본 0.5)
 *   N   : 1주기 샘플 수 = round(T / ctrl_dt)
 * ωe가 변하면 N을 재계산해 버퍼 크기를 자동 조정한다.
 *
 * @param {number} error  - 현재 오차 (d축 또는 q축)
 * @param {object} params - 차량 파라미터 (Q_repetitive, Kr_repetitive, ctrl_dt)
 * @param {object} state  - 반복 제어기 상태 (initRepetitiveControllerState 반환)
 * @returns {number}      - 보상 출력 u_rep
 */
function repetitiveController(error, params, state) {
  // 주기: T = 2π / ωe
  const T_period = (2 * Math.PI) / state.omega_e;
  const N_samples = Math.round(T_period / (params.ctrl_dt || 0.0001));
  
  // 버퍼 크기 조정 (주파수 변화 시)
  // [Bug-2-Fix] error_buffer_d/error_buffer_q 이중 선언 → 단일 error_buffer로 통합
  // motorFOCStep에서 d축/q축 각각 독립된 state 객체(rep_d_state, rep_q_state)를
  // 인자로 넘기므로, state당 버퍼는 1개(error_buffer)면 충분하다.
  // 기존 error_buffer_q는 선언만 되고 읽거나 쓴 적이 없어 q축 RC가 사실상
  // d축 이전 오차를 재사용하는 버그를 유발했음.
  if (N_samples !== state.N_samples) {
    state.error_buffer = new Array(N_samples).fill(0);
    state.N_samples = N_samples;
    state.buffer_idx = 0;
  }
  
  // 학습 게인
  const Q = params.Q_repetitive || 0.98; // 안정성 필터
  const Kr = params.Kr_repetitive || 0.5; // 학습률
  
  // 1주기 전 오차 가져오기
  const error_delayed = state.error_buffer[state.buffer_idx] || 0;
  
  // Repetitive 제어 출력
  const u_rep = Q * error_delayed + Kr * error;
  
  // 버퍼 업데이트
  state.error_buffer[state.buffer_idx] = error;
  state.buffer_idx = (state.buffer_idx + 1) % N_samples;
  
  return u_rep;
}

// ── initRepetitiveControllerState ──
/**
 * 반복 제어기 초기 상태 생성
 *
 * 초기 ωe=100 rad/s(추정값)로 N_samples와 버퍼를 초기화한다.
 * d축/q축 각각 별도의 state 객체를 사용해 독립 학습 버퍼를 유지한다.
 *
 * @param {object} params
 *   params.ctrl_dt - 제어 루프 주기 [s] (기본 0.0001 = 10 kHz)
 * @returns {{ error_buffer: number[], buffer_idx, N_samples, omega_e }}
 */
function initRepetitiveControllerState(params) {
  const omega_e = 100; // 초기 추정
  const T_period = (2 * Math.PI) / omega_e;
  const N_samples = Math.round(T_period / (params.ctrl_dt || 0.0001));
  
  return {
    error_buffer: new Array(N_samples).fill(0),  // [Bug-2-Fix] 단일 버퍼 (d/q state 객체를 별도로 사용)
    buffer_idx: 0,
    N_samples,
    omega_e: omega_e
  };
}

// ── motorFOCStep ──
/**
 * motorFOCStep — dq축 전류 제어 1스텝 (PI 또는 1차 지연)
 *
 * simulation.js의 simLaunch / simReaccel 루프에 중복 산재된 FOC 블록을
 * 단일 함수로 통합. params.foc_current_pi_enable 플래그로 모드 분기.
 *
 * focState 필드:
 *   - Id, Iq          : 현재 dq 전류 [A]
 *   - int_d, int_q    : PI 적분기 상태
 *   - theta_e         : 전기각 [rad] — svpwmModulate 역Park 변환에 사용
 *                       simulation.js에서 omega_e 적분으로 매 스텝 갱신 필요
 *                       누락 시 0 폴백 (SVPWM 섹터 판별 부정확해짐)
 *   - rep_d/q_state   : 반복제어(RC) 버퍼
 *
 * @param {object} focState  - 위 필드 포함
 * @param {number} Id_ref    - d축 전류 지령 [A]
 * @param {number} Iq_ref    - q축 전류 지령 [A]
 * @param {number} omega_e   - 전기 각속도 [rad/s]
 * @param {number} lpm       - 온도 보정 자속 쇄교수 [Wb]
 * @param {number} T_motor   - 모터 온도 [°C]
 * @param {number} T_inv     - 인버터 온도 [°C]
 * @param {object} params    - 차량 파라미터
 * @param {number} dt        - 시간 스텝 [s]
 * @returns {{ Id, Iq, int_d, int_q, theta_e, rep_d_state, rep_q_state,
 *             Vd_cmd, Vq_cmd, V_sat, foc_mode, svpwm }}
 */
function motorFOCStep(focState, Id_ref, Iq_ref, omega_e, lpm, T_motor, T_inv, params, dt) {
  let { Id, Iq, int_d, int_q, rep_d_state, rep_q_state } = focState;
  // [Saturation] 현재 전류에서 포화 인덕턴스 계산 (T_motor: 인버터 온도 기준)
  const sat = saturatedInductance(Id, Iq, params, T_motor);
  const Ld = sat.Ld;
  const Lq = sat.Lq;
  const V_max_foc = (params.V_dc || 400) * (params.fw_v_margin || 0.95) / Math.sqrt(3);
  const I_max = I_max_T(T_inv, params) || params.I_max_rms;

  let Vd_cmd = 0, Vq_cmd = 0;
  let V_sat = false;
  let svpwm = null;        // PI 모드에서만 채워짐, LPF 모드는 null 유지
  let theta_e_est = (focState.theta_e || 0);  // 블록 밖 호이스팅 — LPF에서도 접근 가능

  if (params.foc_current_pi_enable) {
    // ━━ PI 전류 제어 + dq 디커플링 ━━
    const e_d = Id_ref - Id;
    const e_q = Iq_ref - Iq;

    // 적분기 안티와인드업 클램프
    const int_limit = V_max_foc / Math.max(params.Ki_id || 75, 1);
    int_d = Math.max(-int_limit, Math.min(int_limit, (int_d || 0) + e_d * dt));
    int_q = Math.max(-int_limit, Math.min(int_limit, (int_q || 0) + e_q * dt));

    // PI 출력 + 디커플링 피드포워드
    Vd_cmd = (params.Kp_id || 1.51) * e_d + (params.Ki_id || 75) * int_d
             - omega_e * Lq * Iq;
    Vq_cmd = (params.Kp_iq || 3.02) * e_q + (params.Ki_iq || 75) * int_q
             + omega_e * (Ld * Id + lpm);

    // [M3-Fix] 데드타임 전압 왜곡 상시 피드포워드 (항상 활성)
    //   ΔV_d = (2·V_dc·t_dead·f_sw / π)·sign(Id)
    //   ΔV_q = (2·V_dc·t_dead·f_sw / π)·sign(Iq)
    //   근거: 각 상의 평균 전압 오차 ≈ V_dc·t_dead·f_sw (단순 모델)
    //         dq 변환 후 (2/π) 계수 적용 → 저속 토크 왜곡 보정
    {
      const t_dead_ff = params.dead_time    || 2.5e-6;   // [s] 데드타임
      const f_sw_ff   = params.carrier_freq || 10000;    // [Hz] 스위칭 주파수
      const dV_ff     = (2 / Math.PI) * (params.V_dc || 400) * t_dead_ff * f_sw_ff;
      Vd_cmd += dV_ff * Math.sign(Id || 1e-9);
      Vq_cmd += dV_ff * Math.sign(Iq || 1e-9);
    }

    // 데드타임 보상 (고정밀 3상 클락-변환 방식 — opt-in)
    // [Fix-DTC-1] theta_e = atan2(Iq, Id) 오류: 이는 전류벡터 위상각이며 로터 위치 아님
    //   수정: focState.theta_e (omega_e 적분 로터 전기각) 사용
    if (params.deadtime_compensation_enable) {
      const theta_e = focState.theta_e || 0;  // [Fix-DTC-1] 로터 전기각
      const dtc = deadtimeCompensation(Id, Iq, theta_e, params);
      Vd_cmd += dtc.V_d_comp;
      Vq_cmd += dtc.V_q_comp;
    }

    // RC 반복제어 토크 리플 보상
    if (params.rc_ripple_enable) {
      if (!rep_d_state) rep_d_state = initRepetitiveControllerState(params);
      if (!rep_q_state) rep_q_state = initRepetitiveControllerState(params);
      rep_d_state.omega_e = omega_e;
      rep_q_state.omega_e = omega_e;
      const V_d_rep = repetitiveController(e_d, params, rep_d_state);
      const V_q_rep = repetitiveController(e_q, params, rep_q_state);
      // [Fix-RC-1] 1번째 인자가 omega_e → theta_e(로터각)이어야 함
      //   harmonicCompensation(theta_e, omega_e, ...) 서명 확인
      const harm = harmonicCompensation(theta_e_est, omega_e, Id, Iq, params);  // [Fix-RC-1]
      Vd_cmd += V_d_rep + harm.V_d_comp;
      Vq_cmd += V_q_rep + harm.V_q_comp;
    }

    // 전압 변조 — svpwmModulate (SVPWM / DPWM / Overmod 통합)
    // theta_e: omega_e * dt 적분 갱신 (simulation.js가 미전달 시 0에서 시작)
    theta_e_est = ((focState.theta_e || 0) + omega_e * dt) % (2 * Math.PI);
    // [Fix-motor-DPWM] Id_cur을 6번째 명시적 인자로 분리 (params 터널링 제거)
    // 기존: { ...params, Id } 스프레드 → params 계약 오염, 스프레드 비용
    // 수정: svpwmModulate(..., params, Id) → 함수 시그니처에서 의도 명시
    svpwm = svpwmModulate(Vd_cmd, Vq_cmd, theta_e_est, params.V_dc || 400,
      params, Id);
    // 변조 후 dq 전압으로 교체 (Overmod 보정 반영)
    Vd_cmd = svpwm.Vd_mod;
    Vq_cmd = svpwm.Vq_mod;
    V_sat  = svpwm.V_sat;
    // 전압 포화 시 안티와인드업 (적분 동결)
    if (V_sat) {
      int_d -= (Id_ref - Id) * dt;
      int_q -= (Iq_ref - Iq) * dt;
    }

    // dq 전압방정식 적분 (과도상태 전류 동역학)
    // [Fix-dq-1] Euler 적분 순서 오류: Iq 방정식의 'Ld*Id' 항이 이미 갱신된 Id를 참조
    //   수정: Id_old 저장 후 Iq 계산에 사용 → 같은 스텝 내 교차 의존성 제거
    const Rs_eff = Rs_T(T_motor, params);
    const Id_old = Id;  // [Fix-dq-1] Iq 방정식용 이전 스텝 Id 저장
    Id += ((Vd_cmd - Rs_eff * Id + omega_e * Lq * Iq) / Ld) * dt;
    Iq += ((Vq_cmd - Rs_eff * Iq - omega_e * (Ld * Id_old + lpm)) / Lq) * dt;  // [Fix-dq-1]

  } else {
    // ━━ 1차 지연 근사 (LPF 방식, 고속·안정적) ━━
    const tau_d  = Ld / (params.Rs || 0.015);
    const tau_q  = Lq / (params.Rs || 0.015);
    const alpha_d = Math.min(0.99, dt / (tau_d + dt));
    const alpha_q = Math.min(0.99, dt / (tau_q + dt));
    Id = Id + alpha_d * (Id_ref - Id);
    Iq = Iq + alpha_q * (Iq_ref - Iq);
  }

  // 전류 크기 제한
  const I_mag = Math.sqrt(Id * Id + Iq * Iq);
  if (I_mag > I_max) {
    const sc = I_max / I_mag;
    Id *= sc;
    Iq *= sc;
  }

  // svpwm 결과: PI 모드는 위에서 이미 계산한 svpwm 재사용 (중복 호출 제거)
  // [Fix-B02] 기존 2차 svpwmModulate 호출 제거 → 1차 결과(svpwm) 직접 참조
  const svpwm_out = params.foc_current_pi_enable ? svpwm : null;

  return {
    Id, Iq,
    int_d: int_d || 0,
    int_q: int_q || 0,
    // theta_e: PI/LPF 공통 — omega_e * dt 적분 (outer scope theta_e_est 사용)
    theta_e: params.foc_current_pi_enable
      ? theta_e_est
      : (((focState.theta_e || 0) + omega_e * dt) % (2 * Math.PI)),
    rep_d_state,
    rep_q_state,
    Vd_cmd, Vq_cmd,
    V_sat,
    foc_mode: params.foc_current_pi_enable ? 'PI' : 'LPF',
    // SVPWM 변조 결과 (신규 필드 is_sixstep / dpwm_active / M_SIXSTEP 포함)
    svpwm: svpwm_out ? {
      sector:          svpwm_out.sector,
      m_a:             svpwm_out.m_a,
      m_a_raw:         svpwm_out.m_a_raw,
      T1:              svpwm_out.T1,
      T2:              svpwm_out.T2,
      T0:              svpwm_out.T0,
      Va:              svpwm_out.Va,
      Vb:              svpwm_out.Vb,
      Vc:              svpwm_out.Vc,
      duty_A:          svpwm_out.duty_A,
      duty_B:          svpwm_out.duty_B,
      duty_C:          svpwm_out.duty_C,
      V_mag:           svpwm_out.V_mag,
      V_max_linear:    svpwm_out.V_max_linear,
      V_max_sixstep:   svpwm_out.V_max_sixstep,
      M_SIXSTEP:       svpwm_out.M_SIXSTEP,
      pwm_mode_active: svpwm_out.pwm_mode_active,
      V_sat:           svpwm_out.V_sat,
      is_sixstep:      svpwm_out.is_sixstep,
      dpwm_active:     svpwm_out.dpwm_active,
    } : null,
  };
}


// [Fix-motor-DPWM] Id_cur: 명시적 6번째 인자 (DPWM_MAX 자동 선택용)
// 기존: params.Id를 호출부에서 { ...params, Id } 스프레드로 터널링
// 수정: Id_cur을 독립 인자로 분리 → 시그니처에서 의도 명시, params 불변 보장
/**
 * 공간벡터 PWM 변조 (SVPWM / DPWM / 6-step 통합)
 *
 * dq 전압 지령을 3상 duty 사이클로 변환하는 변조기:
 *   1. 역Park (dq → αβ): V_αβ = Park⁻¹(Vd, Vq, θe)
 *   2. Overmodulation 처리 (m_a > 1.0):
 *      - overmod_enable=false: 선형 한계(V_dc/√3) 클램핑
 *      - Overmod I (m_a < M_SIXSTEP): hexagon 경계 투영
 *      - 6-step (m_a ≥ M_SIXSTEP = 2√3/π ≈ 1.1027): 120° 도통 고정
 *   3. 역Clarke (αβ → 3상)
 *   4. SVPWM: T0 균등 배분 / DPWM0/1/MAX: 한 상 duty 클램핑
 *
 * @param {number} Vd     - d축 전압 지령 [V]
 * @param {number} Vq     - q축 전압 지령 [V]
 * @param {number} theta_e - 전기 로터 각도 [rad]
 * @param {number} Vdc    - DC 링크 전압 [V]
 * @param {object} params - 차량 파라미터 (pwm_mode, overmod_enable, dpwm_m_threshold)
 * @param {number} [Id_cur] - d축 전류 [A] (DPWM_MAX 자동 선택에 사용)
 * @returns {{ Va, Vb, Vc, duty_A, duty_B, duty_C, sector, T1, T2, T0,
 *             m_a, m_a_raw, V_mag, V_max_linear, V_max_sixstep, M_SIXSTEP,
 *             pwm_mode_active, V_sat, is_sixstep, dpwm_active, Vd_mod, Vq_mod }}
 */
function svpwmModulate(Vd, Vq, theta_e, Vdc, params, Id_cur) {
  const pwm_mode     = params.pwm_mode      || 'SVPWM';
  const overmod_en   = params.overmod_enable || false;
  // dpwm_m_threshold: DPWM 적용 최소 m_a — 저속·경부하 구간(m_a 낮음) 제외
  const dpwm_thr     = params.dpwm_m_threshold ?? 0.5;

  const sqrt3        = Math.sqrt(3);
  const sqrt3_2      = sqrt3 / 2;
  const V_max_linear = Vdc / sqrt3;         // SVPWM 선형 한계 [V]   = Vdc/sqrt(3)
  const V_max_sixstep= Vdc * 2 / Math.PI;  // 6-step 기본파 피크 [V] = 2Vdc/PI
  // 6-step 경계 m_a: V_max_sixstep / V_max_linear = 2sqrt(3)/PI ≈ 1.1027
  // [Fix-Overmod] 기존 1.053 경계는 오류 → 2sqrt(3)/PI = 1.1027로 수정
  const M_SIXSTEP    = V_max_sixstep / V_max_linear; // = 2*sqrt(3)/PI ≈ 1.1027

  const V_mag    = Math.sqrt(Vd * Vd + Vq * Vq);
  const m_a_raw  = V_mag / Math.max(V_max_linear, 1e-6);

  // ── Step 1: 역Park 변환 (dq → αβ) ──────────────────────────────────
  const cos_t  = Math.cos(theta_e);
  const sin_t  = Math.sin(theta_e);
  let V_alpha  = Vd * cos_t - Vq * sin_t;
  let V_beta   = Vd * sin_t + Vq * cos_t;

  // ── Step 2: Overmodulation 처리 ─────────────────────────────────────
  let m_a             = m_a_raw;
  let pwm_mode_active = pwm_mode;
  let V_sat           = m_a_raw > 1.0;
  let is_sixstep      = false;

  // 6-step 섹터별 고정 duty 패턴 — 표준 120° 도통 (sector 0~5)
  const SIX_STEP_DUTY = [
    [1, 0, 0],  // sector 0 (0~60°)
    [1, 1, 0],  // sector 1 (60~120°)
    [0, 1, 0],  // sector 2 (120~180°)
    [0, 1, 1],  // sector 3 (180~240°)
    [0, 0, 1],  // sector 4 (240~300°)
    [1, 0, 1],  // sector 5 (300~360°)
  ];

  if (m_a_raw > 1.0) {
    if (overmod_en) {
      if (m_a_raw >= M_SIXSTEP) {
        // ── 완전 6-step (m_a >= M_SIXSTEP) ──────────────────────────
        // 방향 유지, 기본파 크기 V_max_sixstep으로 고정
        const scale     = V_max_sixstep / Math.max(V_mag, 1e-6);
        V_alpha        *= scale;
        V_beta         *= scale;
        m_a             = M_SIXSTEP;
        pwm_mode_active = 'SIXSTEP';
        is_sixstep      = true;
        // [Fix-6step] theta_ab를 is_sixstep 분기에서 미리 계산해 duty 직접 결정
        // Step 6의 sector LUT(경계에서 오차 가능)보다 theta_ab 기반이 정확함
        // theta_ab는 역Park 후 αβ 벡터의 실제 각도 (0 ~ 2π)
        // Step 6에서 is_sixstep=true이면 SIX_STEP_DUTY[theta_ab_sec] 사용
      } else {
        // ── Overmod I (1.0 < m_a < M_SIXSTEP): hexagon 경계 투영 ────
        // hexagon 반지름: r_hex(θ) = (Vdc/sqrt(3)) / cos(θ_sector)
        // θ_sector: 현재 αβ 각도와 해당 섹터 중심각 사이의 편차
        const theta_ab  = Math.atan2(V_beta, V_alpha);
        const sec_idx   = Math.floor(
          (((theta_ab % (2 * Math.PI)) + 2 * Math.PI) % (2 * Math.PI)) / (Math.PI / 3)
        );
        const theta_mid = sec_idx * Math.PI / 3 + Math.PI / 6;
        const theta_sec = theta_ab - theta_mid;
        // cos(theta_sec) 하한 0.5 클램프: 섹터 경계(±60°)에서 발산 방지
        const r_hex     = (Vdc / sqrt3) / Math.max(Math.cos(theta_sec), 0.5);
        // Overmod I: hexagon 경계까지 전압 허용 (hexagon 클램프)
        // V_mag를 r_hex 이하로 제한 — 방향은 유지, 크기만 조정
        // r_hex는 섹터 중앙(V_max_linear)~섹터 경계(1.1547*V_max_linear) 사이
        const r_clamped = Math.min(V_mag, r_hex);
        const scale     = r_clamped / Math.max(V_mag, 1e-6);
        V_alpha        *= scale;
        V_beta         *= scale;
        m_a             = Math.sqrt(V_alpha * V_alpha + V_beta * V_beta) / V_max_linear;
        pwm_mode_active = 'OVERMOD';
      }
    } else {
      // ── overmod_enable=false: 선형 한계 클램프 ─────────────────────
      const scale     = V_max_linear / Math.max(V_mag, 1e-6);
      V_alpha        *= scale;
      V_beta         *= scale;
      m_a             = 1.0;
      pwm_mode_active = pwm_mode + '_SAT';
    }
  }

  // ── Step 3: 역Clarke 변환 (αβ → 3상) ───────────────────────────────
  //   Va =  Vα,  Vb = -½Vα + (√3/2)Vβ,  Vc = -½Vα - (√3/2)Vβ
  let Va = V_alpha;
  let Vb = -0.5 * V_alpha + sqrt3_2 * V_beta;
  let Vc = -0.5 * V_alpha - sqrt3_2 * V_beta;

  // ── Step 4: 섹터 판별 ────────────────────────────────────────────────
  const sA = Va >= 0 ? 1 : 0;
  const sB = Vb >= 0 ? 1 : 0;
  const sC = Vc >= 0 ? 1 : 0;
  const sector_lut = [0, 1, 6, 2, 5, 0, 4, 3]; // idx = sA + 2*sB + 4*sC
  const sector = (sector_lut[sA + 2 * sB + 4 * sC] - 1 + 6) % 6;

  // ── Step 5: 스위칭 시간 T1/T2/T0 계산 ──────────────────────────────
  const vn = 2 / Vdc;
  const x  = V_beta  * vn;
  const y  = (V_alpha * sqrt3_2 + V_beta * 0.5) * vn;
  const z  = (V_alpha * sqrt3_2 - V_beta * 0.5) * vn;

  const T_sector = [
    [  x, -z ],  // sector 0
    [  y,  x ],  // sector 1
    [ -z,  y ],  // sector 2
    [ -x,  z ],  // sector 3
    [ -y, -x ],  // sector 4
    [  z, -y ],  // sector 5
  ];
  let T1_raw = T_sector[sector][0];
  let T2_raw = T_sector[sector][1];

  let T0     = Math.max(0, 1 - Math.abs(T1_raw) - Math.abs(T2_raw));
  T1_raw     = Math.max(0, T1_raw);
  T2_raw     = Math.max(0, T2_raw);

  const T_sum   = T1_raw + T2_raw + T0;
  const T1      = T_sum > 0 ? T1_raw / T_sum : 0;
  const T2      = T_sum > 0 ? T2_raw / T_sum : 0;
  const T0_norm = T_sum > 0 ? T0    / T_sum : 1;

  // ── Step 6: duty 사이클 계산 ────────────────────────────────────────
  let duty_A, duty_B, duty_C;
  let dpwm_active = false;

  if (is_sixstep) {
    // ── 완전 6-step: theta_ab 기반 섹터로 duty 결정 ──────────────────
    // [Fix-6step] Step 4의 LUT sector는 경계에서 오차 발생 가능
    //   → αβ 각도(theta_ab)에서 직접 섹터 계산 (0~2π 정규화 후 /60°)
    const theta_ab_raw   = Math.atan2(V_beta, V_alpha);
    const theta_ab_norm  = ((theta_ab_raw % (2 * Math.PI)) + 2 * Math.PI) % (2 * Math.PI);
    const sec_sixstep    = Math.floor(theta_ab_norm / (Math.PI / 3)) % 6;
    [duty_A, duty_B, duty_C] = SIX_STEP_DUTY[sec_sixstep];
  } else {
    // ── SVPWM / DPWM 공통 기본 duty 계산 ─────────────────────────────
    const d_A_base = 0.5 + V_alpha / Vdc;
    const d_B_base = 0.5 + (-0.5 * V_alpha + sqrt3_2 * V_beta) / Vdc;
    const d_C_base = 0.5 + (-0.5 * V_alpha - sqrt3_2 * V_beta) / Vdc;

    // DPWM 적용 조건:
    //   (1) DPWM 모드 선택  AND
    //   (2) m_a >= dpwm_m_threshold (저속·경부하 구간 제외)
    //   [Fix-DPWM-Threshold] 기존: 임계값 조건 없음 → m_a<thr 구간도 DPWM 적용됨
    const want_dpwm = pwm_mode === 'DPWM1' || pwm_mode === 'DPWM0' || pwm_mode === 'DPWM_MAX';
    const use_dpwm  = want_dpwm && (m_a_raw >= dpwm_thr);

    if (use_dpwm) {
      dpwm_active = true;
      // DPWM_MAX: Id 부호 기반 DPWM1/DPWM0 자동 선택
      //   MTPA 구간 (Id >= 0): DPWM1 → 양 피크 클램핑, 도통 손실 최소화
      //   약계자 구간 (Id < 0): DPWM0 → 음 피크 클램핑, 스위칭 손실 최소화
      //   [Fix-DPWM_MAX] 기존: DPWM1과 동일 코드 → Id 기반 자동 선택으로 개선
      // [Fix-motor-DPWM] Id_cur: 명시적 인자로 전달됨 (params.Id 조회 제거)
      // 미전달(undefined) 시 0 폴백 → DPWM_MAX → DPWM1 기본 동작 유지
      const _Id_cur = Id_cur !== undefined ? Id_cur : 0;
      const use_pos = pwm_mode === 'DPWM1' ||
                      (pwm_mode === 'DPWM_MAX' && _Id_cur >= 0);
      const use_neg = pwm_mode === 'DPWM0' ||
                      (pwm_mode === 'DPWM_MAX' && _Id_cur < 0);

      if (use_pos) {
        // DPWM1: 최대 상을 1로 클램핑 → 양 피크 구간 스위칭 정지
        const d_max  = Math.max(d_A_base, d_B_base, d_C_base);
        const offset = 1 - d_max;
        duty_A = Math.min(1, Math.max(0, d_A_base + offset));
        duty_B = Math.min(1, Math.max(0, d_B_base + offset));
        duty_C = Math.min(1, Math.max(0, d_C_base + offset));
        pwm_mode_active = m_a_raw < M_SIXSTEP ? 'DPWM1' : pwm_mode_active;
      } else if (use_neg) {
        // DPWM0: 최소 상을 0으로 클램핑 → 음 피크 구간 스위칭 정지
        const d_min  = Math.min(d_A_base, d_B_base, d_C_base);
        const offset = -d_min;
        duty_A = Math.min(1, Math.max(0, d_A_base + offset));
        duty_B = Math.min(1, Math.max(0, d_B_base + offset));
        duty_C = Math.min(1, Math.max(0, d_C_base + offset));
        pwm_mode_active = m_a_raw < M_SIXSTEP ? 'DPWM0' : pwm_mode_active;
      } else {
        // DPWM_MAX 폴백 (Id 정보 없음 → DPWM1)
        const d_max  = Math.max(d_A_base, d_B_base, d_C_base);
        const offset = 1 - d_max;
        duty_A = Math.min(1, Math.max(0, d_A_base + offset));
        duty_B = Math.min(1, Math.max(0, d_B_base + offset));
        duty_C = Math.min(1, Math.max(0, d_C_base + offset));
        pwm_mode_active = m_a_raw < M_SIXSTEP ? 'DPWM_MAX' : pwm_mode_active;
      }
    } else {
      // ── SVPWM: T0 균등 배분 (대칭 센터-정렬) ─────────────────────────
      duty_A = Math.min(1, Math.max(0, d_A_base + T0_norm / 2));
      duty_B = Math.min(1, Math.max(0, d_B_base + T0_norm / 2));
      duty_C = Math.min(1, Math.max(0, d_C_base + T0_norm / 2));
      if (want_dpwm && m_a_raw < dpwm_thr) {
        // DPWM 요청이나 임계값 미달 → SVPWM 폴백 (저속·경부하 보호)
        pwm_mode_active = 'SVPWM_LO';
      } else {
        pwm_mode_active = m_a_raw <= 1.0 ? 'SVPWM' : pwm_mode_active;
      }
    }
  }

  // ── duty → 상전압 역산 (중성점 기준) ────────────────────────────────
  const V_neutral = (duty_A + duty_B + duty_C) / 3 * Vdc;
  const Va_out    = duty_A * Vdc - V_neutral;
  const Vb_out    = duty_B * Vdc - V_neutral;
  const Vc_out    = duty_C * Vdc - V_neutral;

  // ── 변조 후 dq 전압 역산 (Overmod/6-step 보정 반영) ─────────────────
  const Vd_mod = V_alpha * cos_t + V_beta * sin_t;
  const Vq_mod =-V_alpha * sin_t + V_beta * cos_t;

  return {
    Va:              +Va_out.toFixed(4),
    Vb:              +Vb_out.toFixed(4),
    Vc:              +Vc_out.toFixed(4),
    V_alpha:         +V_alpha.toFixed(4),
    V_beta:          +V_beta.toFixed(4),
    duty_A:          +duty_A.toFixed(4),
    duty_B:          +duty_B.toFixed(4),
    duty_C:          +duty_C.toFixed(4),
    sector,
    T1:              +T1.toFixed(4),
    T2:              +T2.toFixed(4),
    T0:              +T0_norm.toFixed(4),
    m_a:             +m_a.toFixed(4),
    m_a_raw:         +m_a_raw.toFixed(4),
    V_mag:           +V_mag.toFixed(3),
    V_max_linear:    +V_max_linear.toFixed(3),
    V_max_sixstep:   +V_max_sixstep.toFixed(3),
    M_SIXSTEP:       +M_SIXSTEP.toFixed(6),
    pwm_mode_active,
    V_sat,
    is_sixstep,
    dpwm_active,
    Vd_mod:          +Vd_mod.toFixed(4),
    Vq_mod:          +Vq_mod.toFixed(4),
  };
}

// ── computeMotorOperatingPointMap ──
/**
 * MTPA/FW/MTPV 동작점 2D 맵 생성 — [RevD] Motor Operating Point Optimizer
 *
 * 속도-토크 격자(n_omega × n_T)에서 각 점의 FOC 전류 지령(Id, Iq),
 * 동작 영역(MTPA/FW/MTPV), 전기 효율(η_elec)을 계산한다.
 *
 * 활용:
 *   - MotorGroupContent의 MTPA→MTPV 전환 경계선 오버레이 시각화
 *   - 효율 등고선(contour) 맵, 최적 동작점 탐색
 *
 * @param {object} params      - 차량 파라미터
 * @param {object} [options]   - 격자 설정
 *   @param {number} [options.omega_min=10]      최소 기계각속도 [rad/s]
 *   @param {number} [options.omega_max=1500]    최대 기계각속도 [rad/s]
 *   @param {number} [options.T_min=5]           최소 토크 [N·m]
 *   @param {number} [options.T_max]             최대 토크 [N·m] (기본: params.T_max_motor)
 *   @param {number} [options.n_omega=20]        속도 격자 수
 *   @param {number} [options.n_T=15]            토크 격자 수
 *   @param {number} [options.T_motor=25]        모터 온도 [°C]
 * @returns {Array<{omega_m, omega_e, T_req, T_actual, Id, Iq, regime,
 *                  eta_elec, V_mag, V_max, I_mag, V_util}>}
 *
 * 참고: Pellegrino et al. (2012), "Unified Design Procedure … IPMSM"
 *       IEEE Trans. Ind. Electron., 59(9), 3589–3601
 */
function computeMotorOperatingPointMap(params, options = {}) {
  const omega_min = options.omega_min  ?? 10;
  const omega_max = options.omega_max  ?? Math.min(1500, params.omega_max_motor || 1500);
  const T_min     = options.T_min      ?? 5;
  const T_max_opt = options.T_max      ?? (params.T_max_motor || 400);
  const n_omega   = options.n_omega    ?? 20;
  const n_T       = options.n_T        ?? 15;
  const T_motor   = options.T_motor    ?? 25;
  const V_dc      = params.V_dc        || 400;
  const p         = (params.poles || 8) / 2;

  const results = [];

  for (let io = 0; io < n_omega; io++) {
    // 로그 스케일 분포: 저속 고해상도 유지
    const t_o    = io / Math.max(n_omega - 1, 1);
    const omega_m = omega_min * Math.pow(omega_max / omega_min, t_o);
    const omega_e = omega_m * p;

    for (let it = 0; it < n_T; it++) {
      const T_req = T_min + (T_max_opt - T_min) * it / Math.max(n_T - 1, 1);

      // ── FOC 전류 지령 산출 (MTPA → FW → MTPV 자동 분기) ──
      const ref          = computeCurrentReferenceWithMTPV(omega_e, T_req, V_dc, params, T_motor);
      const { Id, Iq, regime } = ref;

      // ── 실제 토크 (포화 인덕턴스 반영) ──
      const lpm    = lambda_pm_T(T_motor, params);
      const sat    = saturatedInductance(Id, Iq, params, T_motor);
      const T_actual = 1.5 * p * (lpm * Iq + (sat.Ld - sat.Lq) * Id * Iq);

      // ── 손실 및 효율 ──
      const losses   = computeMotorLosses(omega_m, T_actual, Id, Iq, params, T_motor);
      const P_mech   = Math.max(0, T_actual * omega_m);
      const P_elec   = P_mech + losses.P_total;
      const eta_elec = P_elec > 0.5 ? Math.min(0.99, P_mech / P_elec) : 0;

      // ── 전압 크기 (전압 제약 활용률 계산) ──
      const Rs   = Rs_T(T_motor, params);
      const V_d  = Rs * Id - omega_e * sat.Lq * Iq;
      const V_q  = Rs * Iq + omega_e * (sat.Ld * Id + lpm);
      const V_mag = Math.sqrt(V_d * V_d + V_q * V_q);
      const V_max = V_dc * (params.fw_v_margin || 0.95) / Math.sqrt(3);
      const I_mag = Math.sqrt(Id * Id + Iq * Iq);

      results.push({
        omega_m:   +omega_m.toFixed(2),
        omega_e:   +omega_e.toFixed(2),
        T_req:     +T_req.toFixed(2),
        T_actual:  +T_actual.toFixed(2),
        Id:        +Id.toFixed(2),
        Iq:        +Iq.toFixed(2),
        regime,
        eta_elec:  +eta_elec.toFixed(4),
        V_mag:     +V_mag.toFixed(2),
        V_max:     +V_max.toFixed(2),
        I_mag:     +I_mag.toFixed(2),
        // 전압 활용률 (0→1: 기저속도 이하, 1: 전압 한계 도달)
        V_util:    +(V_mag / Math.max(V_max, 1)).toFixed(3),
        // 전류 활용률
        I_util:    +(I_mag / Math.max(params.I_max_rms || 600, 1)).toFixed(3),
      });
    }
  }
  return results;
}

// ── gearRatioEfficiencySearch ──
/**
 * 기어비별 WLTP 효율 탐색기 — [RevD] Efficiency-Map Gear Ratio Explorer
 *
 * 주어진 기어비 배열에 대해 WLTP 대표 동작점에서의 모터 효율 및
 * 추정 에너지 소비를 계산하여 최적 기어비를 식별한다.
 *
 * 동작:
 *   각 gear_ratio에 대해 WLTP 대표 속도/도로저항 전력을
 *   이용해 모터 동작점을 산출하고, computeMotorLosses 기반으로 효율을 평가한다.
 *
 * 내장 WLTP 대표 동작점 (5개, Phase I~IV 가중):
 *   Low 20 km/h · Medium 45 km/h · High 75 km/h · Extra-High 110 km/h · Regen 60 km/h
 *
 * @param {object}   params                        - 차량 파라미터
 * @param {number[]} [gear_ratios]                 - 탐색 기어비 배열 (기본: 5~15 스텝 0.5)
 * @param {object}   [options]
 *   @param {Array<{v_kmh, P_road_W, w_time}>} [options.cycle_points]
 *                                                  - WLTP 사이클 대표점 (없으면 내장 표준 5점)
 *   @param {number} [options.T_motor=25]           - 모터 온도 [°C]
 * @returns {Array<{gear_ratio, eta_avg, eta_weighted, energy_Wh_km, peak_eta, optimal}>}
 *
 * 참고: Pellegrino et al. (2012); Morimoto et al. (1994)
 *       WLTP 가중 대표점: UNECE GTR 15 Annex B1 (2015)
 */
function gearRatioEfficiencySearch(params, gear_ratios, options = {}) {
  // 기본 기어비 배열: 5.0 ~ 15.0 (0.5 스텝)
  const grs      = gear_ratios ?? Array.from({ length: 21 }, (_, i) => 5.0 + i * 0.5);
  const T_motor  = options.T_motor ?? 25;
  const V_dc     = params.V_dc     || 400;
  const r_wheel  = params.r_wheel  || 0.334;
  const eta_dl   = params.eta_driveline || 0.95;
  const p_poles  = (params.poles   || 8) / 2;

  // WLTP 대표 동작점 (UNECE GTR 15 기준 Phase 가중 평균)
  const cycle_pts = options.cycle_points ?? [
    { v_kmh:  20, P_road_W:  3500, w_time: 0.255 },  // Low phase (589 s)
    { v_kmh:  45, P_road_W:  8800, w_time: 0.211 },  // Medium phase (433 s)
    { v_kmh:  75, P_road_W: 16500, w_time: 0.217 },  // High phase (455 s)
    { v_kmh: 110, P_road_W: 34200, w_time: 0.185 },  // Extra-High (323 s)
    { v_kmh:  60, P_road_W: 11000, w_time: 0.132 },  // Regen/accel mix
  ];
  const total_w = cycle_pts.reduce((s, c) => s + c.w_time, 0);

  const searchResults = grs.map(gr => {
    let sum_eta   = 0;
    let sum_w_eta = 0;
    let n_valid   = 0;
    let peak_eta  = 0;
    let sum_P_w   = 0;  // 가중 전기 전력 합산 [W]
    let sum_v_w   = 0;  // 가중 속도 합산 [m/s]

    cycle_pts.forEach(pt => {
      const v_ms    = pt.v_kmh / 3.6;
      const w_t     = pt.w_time;
      if (v_ms < 0.5) return;  // 정지 구간 스킵

      // 기어비 → 모터 각속도
      const omega_wheel = v_ms / r_wheel;
      const omega_m     = omega_wheel * gr;
      const omega_e     = omega_m * p_poles;

      // 도로저항 → 모터 토크 요구
      const T_req_wheel  = pt.P_road_W / Math.max(omega_wheel, 0.01);
      const T_req_motor  = T_req_wheel / (gr * eta_dl);
      const T_req        = Math.min(
        (params.T_max_motor || 400) * 0.98,
        Math.max(1, T_req_motor)
      );

      // FOC 전류 지령
      const ref          = computeCurrentReferenceWithMTPV(omega_e, T_req, V_dc, params, T_motor);
      const { Id, Iq }   = ref;

      // 실제 토크 & 출력
      const lpm    = lambda_pm_T(T_motor, params);
      const sat    = saturatedInductance(Id, Iq, params, T_motor);
      const T_act  = 1.5 * p_poles * (lpm * Iq + (sat.Ld - sat.Lq) * Id * Iq);
      const P_mech = Math.max(0, T_act * omega_m);

      // 손실 & 전기 효율
      const losses   = computeMotorLosses(omega_m, T_act, Id, Iq, params, T_motor);
      const P_elec   = P_mech + losses.P_total;
      const eta      = P_elec > 10 ? Math.min(0.99, P_mech / P_elec) : 0;

      sum_eta   += eta;
      sum_w_eta += eta * w_t;
      sum_P_w   += P_elec * w_t;
      sum_v_w   += v_ms * w_t;
      n_valid++;
      if (eta > peak_eta) peak_eta = eta;
    });

    const eta_avg    = n_valid > 0 ? sum_eta / n_valid : 0;
    const eta_w      = sum_w_eta / total_w;
    // Wh/km = 가중 평균 전력 / 가중 평균 속도 / 3600
    const v_avg_ms   = sum_v_w / total_w;
    const P_avg_W    = sum_P_w / total_w;
    const energy_Wh_km = v_avg_ms > 0.1 ? (P_avg_W / v_avg_ms) / 3.6 : 0;

    return {
      gear_ratio:    gr,
      eta_avg:       +eta_avg.toFixed(4),
      eta_weighted:  +eta_w.toFixed(4),
      energy_Wh_km:  +energy_Wh_km.toFixed(2),
      peak_eta:      +peak_eta.toFixed(4),
      optimal:       false,
    };
  });

  // 최적 기어비 마킹 (WLTP 가중 효율 최고점)
  const best_eta = Math.max(...searchResults.map(r => r.eta_weighted));
  searchResults.forEach(r => { r.optimal = (r.eta_weighted === best_eta); });

  return searchResults;
}

/**
 * FOC(Field-Oriented Control) 초기 상태 생성
 *
 * motorFOCStep()에 전달되는 상태 객체를 초기화한다.
 * PI 적분기(int_d, int_q), 로터 전기각(theta_e),
 * 반복제어 버퍼(rep_d/q_state)를 포함한다.
 *
 * @returns {{ Id, Iq, int_d, int_q, theta_e, rep_d_state, rep_q_state,
 *             Vd_cmd, Vq_cmd, V_sat, foc_mode }}
 */
function initFOCState() {
  return {
    Id: 0, Iq: 0,
    int_d: 0, int_q: 0,
    theta_e: 0,          // 전기각 [rad] — omega_e * dt 적분으로 갱신
    rep_d_state: null,
    rep_q_state: null,
    Vd_cmd: 0, Vq_cmd: 0,
    V_sat: false,
    foc_mode: 'LPF',
  };
}

// ── computeKuPhysics ──
/**
 * 언더스티어 구배 Ku 물리 계산 (차량 동역학)
 *
 * 선형 2자유도 모델의 요레이트 전달함수에서 Ku를 계산한다:
 *   요레이트: ψ̇ = v / (L + Ku·v²) · δ,  Ku [s²/m]
 *   Ku = (m / (Caf·Car)) × (l_f·Caf − l_r·Car) / L
 *   L = l_f + l_r (휠베이스 [m])
 * ku_auto_calc=false이면 params.K_understeer를 직접 반환한다.
 *
 * @param {object} params
 *   params.ku_auto_calc - true이면 물리 계산, false이면 K_understeer 직접 사용
 *   params.m    - 차량 질량 [kg]
 *   params.l_f  - 앞 축 ~중심 거리 [m]
 *   params.l_r  - 뒤 축 ~중심 거리 [m]
 *   params.Cf   - 전축 코너링 강성 [N/rad]
 *   params.Cr   - 후축 코너링 강성 [N/rad]
 * @returns {number}  - 언더스티어 구배 Ku [s²/m]
 */
function computeKuPhysics(params) {
  if (!params.ku_auto_calc) return params.K_understeer || 0.001;
  const m    = params.m      || 1850;
  const l_f  = params.l_f    || 1.35;
  const l_r  = params.l_r    || 1.525;
  const Caf  = params.Cf     || 80000;  // front axle cornering stiffness [N/rad]
  const Car  = params.Cr     || 90000;  // rear axle cornering stiffness [N/rad]
  // [A21-18-07] B-07 수정: Ku 정규화 — 요레이트 공식 ψ̇=v/(L+Ku·v²)·δ 에서
  // Ku 단위 = s²/m 이어야 하므로 L(휠베이스)로 나눔 필요
  // 기존: Ku = m*(l_f/Car - l_r/Caf)  →  수정: Ku = m*(l_f/Car - l_r/Caf) / L
  // ku_auto_calc=true + 고속 시 분모 역전 방지
  const L  = l_f + l_r;
  const Ku = (m / (Caf * Car)) * (l_f * Caf - l_r * Car) / L;
  return Ku;
}

export {
  Rs_T,
  lambda_pm_T,
  I_max_T,
  T_max_motor_T,
  motorTorqueSpeed,
  // computeMTPV: [Export-Cleanup] computeCurrentReferenceWithMTPV 내부 전용 → external export 불필요
  computeMTPA,                       // ✅ RevB: 독립 MTPA 함수 export 추가
  computeCurrentReferenceWithMTPV,
  // computeDeadtimeError: [Export-Cleanup] deadtimeCompensation 내부 전용 → external export 불필요
  deadtimeCompensation,
  steinmetzIronLoss_v3,
  computeInverterLosses,
  computeMotorLosses,
  getLUTPkScale,
  validateTractionLUT,
  backEMFObserver,
  mrasObserver,                      // ✅ RevB: 명시적 MRAS 관측기 export 추가
  hfiSensorlessStep,
  sensorlessObserver,
  initSensorlessState,
  saturatedInductance,
  computeTorqueRipple,
  harmonicCompensation,
  repetitiveController,
  initRepetitiveControllerState,
  svpwmModulate,
  motorFOCStep,
  initFOCState,
  computeKuPhysics,
  computeMotorOperatingPointMap,  // ✅ RevD: MTPA/FW/MTPV 동작점 2D 맵 생성
  gearRatioEfficiencySearch,      // ✅ RevD: 기어비별 WLTP 효율 탐색기
};